import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTenantTheme } from '../../contexts/TenantThemeContext';
import { useParams, useNavigate, useLocation } from 'react-router';
import api from '../../services/api';
import liveChatApi from '../../services/liveChatApi';
import { toast } from 'sonner';
import {
  Wallet, LogOut, Copy, ArrowUpCircle, ArrowDownCircle,
  CheckCircle2, XCircle, MessageCircle, Send, Search, Sparkles,
  Crown, RefreshCw, Star, KeyRound, Gamepad2, Phone, Mail, Lock,
  Eye, EyeOff, ChevronDown, ChevronUp, MessagesSquare, Facebook, ChevronLeft,
  BadgeCheck, Pencil, X, CircleUser, Camera, Check, Gift, CircleDollarSign,
  Trophy, Bell, Megaphone, Gem, CalendarDays, Rocket, Headphones, Settings,
  Plus
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { formatFP } from '../../lib/money';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle
} from '../../components/ui/sheet';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from '../../components/ui/dialog';
import {
  Popover, PopoverContent, PopoverTrigger
} from '../../components/ui/popover';
import useNotificationsWS from '../../hooks/useNotificationsWS';

// Extracted player-app sub-components — see ./components/* for each.
import AddGamePanel from './components/AddGamePanel';
import CustomGameForm from './components/CustomGameForm';
import { FacebookGlyph, DiscordGlyph, TelegramGlyph, InstagramGlyph, YoutubeGlyph, TikTokGlyph } from './components/Glyphs';
import { tap } from './components/playerHelpers';
import PlayerActionNudge from './components/PlayerActionNudge';
import PlayerLiquidNavigation, { playerNavigationBase } from './components/PlayerLiquidNavigation';
import { playerLobbyKey, selectPlayerSession, readPlayerLobby, savePlayerLobby, playerSessionRevision, playerRecoveryPending, PLAYER_IDLE_EVENT } from '../../services/playerLobbySession';
import { PlayerConnectionSkeleton } from '../../components/PlayerConnectionGate';

const GAME_TOAST_PREFIX = 'game-action-feedback';
const cleanNotificationValue = (value) => String(value || '').trim();
const gameNotificationToastId = (notification = {}) => {
  const metadata = notification.metadata || {};
  const identity = cleanNotificationValue(notification.id)
    || [
      cleanNotificationValue(metadata.event),
      cleanNotificationValue(metadata.game_id),
      cleanNotificationValue(metadata.account_id),
      cleanNotificationValue(notification.created_at),
    ].filter(Boolean).join(':')
    || 'unknown';
  return `${GAME_TOAST_PREFIX}:${identity}`;
};
const gameNotificationToastTitle = (notification = {}, fallbackTitle = 'Game update') => {
  const metadata = notification.metadata || {};
  const event = cleanNotificationValue(metadata.event).toLowerCase();
  const gameName = cleanNotificationValue(metadata.game_name);
  if (gameName && event === 'game_load_success') return `${gameName} Load Success 🎮`;
  if (gameName && event === 'game_redeem_success') return `${gameName} Redeem Success`;
  return cleanNotificationValue(fallbackTitle) || 'Game update';
};
import MyGamesSection from './components/MyGames';
import WalletFlow, { FullScreenWhitePanel, Stepper, Row, PaymentMethodLogo } from './components/WalletFlow';
import { ResetPasswordPanel, EditProfilePanel } from './components/ProfilePanels';
import { AnnouncementsPanel, NotificationsPanel, AchievementsPanel } from './components/SidePanels';
import ProfileFullScreen from './components/ProfileFullScreen';
import PromoBanners from './components/PromoBanners';
import LoginAnnouncementPopup from './components/LoginAnnouncementPopup';
import PlayerApkUpdatePrompt from './components/PlayerApkUpdatePrompt';
import BalanceDetailPanel from './components/BalanceDetailPanel';
import CashoutSpeedPicker from './components/CashoutSpeedPicker';
import PriorityPayoutFlow from './components/PriorityPayoutFlow';
import ChatPanel from './components/ChatPanel';
import SupportMenu from './components/SupportMenu';
import TimedHomeNudgeStack from './components/TimedHomeNudgeStack';
import SafeImage from '../../components/SafeImage';
import { canOfferInstall, openInstallPrompt } from '../../components/InstallPrompt';
import { tenantFaviconHref } from '../../components/TenantManifestLink';
import {
  getNativePlayerBridge,
  isNativePlayerRuntime,
  playerApkPath,
} from '../../lib/playerApk';
import referAndEarnGiftCutout from '../../assets/refer-and-earn-gift-cutout.png';
import {
  clearStoredPhoneVerificationAttempt,
  isPhoneVerified,
} from '../../lib/phoneVerification';

// Owner re-enabled the redesigned home setup cards; eligibility checks stay per device/user.
const HOME_PROMOTION_NUDGES_ENABLED = true;
const PENDING_GAMES_STORAGE_KEY = 'vl_pending_game_accounts';
const FAVORITES_STORAGE_KEY = 'vl_favs';
const PUSH_NOTIFICATIONS_STORAGE_KEY = 'vl_push_notifications_enabled_at';
const NATIVE_PUSH_NOTIFICATIONS_STORAGE_KEY = 'vl_native_push_notifications_enabled_at';
const FCM_TOKEN_REGISTRATION_STORAGE_KEY = 'vl_native_fcm_token_registered_at';
const APP_INSTALL_SIGNAL_STORAGE_KEY = 'vl_app_install_signal_recorded_at';
const FCM_TOKEN_REGISTER_MIN_INTERVAL_MS = 6 * 60 * 60 * 1000;
const PUSH_SW_URL = '/sw.js';
const PLAYER_HISTORY_GUARD_DEPTH = 40;
const PLAYER_DATA_MIN_LOADING_MS = 700;
const GAME_CREATE_CARD_TIMEOUT_MS = 2 * 60 * 1000;
const BRANDING_REFRESH_INTERVAL_MS = 12000;
const PLAYER_UI_ACCENT_BORDER = 'color-mix(in oklab, var(--vl-profile-accent, var(--vl-neon, #22FF88)) 48%, transparent)';
const PLAYER_UI_ACCENT_GLOW = 'color-mix(in oklab, var(--vl-profile-accent, var(--vl-neon, #22FF88)) 28%, transparent)';
const HALF_SHEET_DIALOG_STYLE = {
  background: 'var(--vl-bg, #000000)',
  borderColor: PLAYER_UI_ACCENT_BORDER,
  boxShadow: `0 -18px 48px rgba(0,0,0,0.58), 0 0 34px ${PLAYER_UI_ACCENT_GLOW}`,
  color: '#FFFFFF',
  position: 'fixed',
  left: '50%',
  right: 'auto',
  top: 'auto',
  bottom: 0,
  transform: 'translateX(-50%)',
  width: 'min(100vw, 520px)',
  maxWidth: '520px',
  maxHeight: '56dvh',
  overflowY: 'auto',
  borderRadius: '28px 28px 0 0',
};

const emptyPlayerData = () => ({ games: [], methods: [], transactions: [], accounts: [] });

const isLocalDemoVipAlertPreview = () => {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  return ['127.0.0.1', 'localhost', '::1'].includes(host) && /^\/demo(\/|$)/.test(window.location.pathname || '');
};

const makeDemoVipRewardAlert = () => ({
  id: 'demo-vip-reward-alert',
  title: 'VIP Reward available',
  message: 'Wallet cashback and Freeplay bonus are ready. Tap to claim inside VIP Rewards.',
  category: 'bonus',
  type: 'success',
  action_url: '/app/explore/vip-rewards',
  read: false,
  is_read: false,
  created_at: new Date().toISOString(),
  metadata: {
    event: 'vip_reward_available',
    vip_claim_id: 'demo-vip-cashback',
    claim_type: 'cashback',
    reward_cash: 0.75,
  },
});

const scopedPlayerStorageKey = (base, tenantSlug, userId) => (
  userId ? `${base}:${tenantSlug || 'global'}:${userId}` : base
);

const hashFcmToken = (token) => {
  const raw = String(token || '');
  let hash = 0;
  for (let i = 0; i < raw.length; i += 1) {
    hash = Math.imul(31, hash) + raw.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash).toString(36);
};

const isLoopbackHost = () => {
  if (typeof window === 'undefined') return false;
  return ['localhost', '127.0.0.1'].includes(window.location.hostname);
};

const isLocalDemoPreview = () => {
  if (typeof window === 'undefined') return false;
  return isLoopbackHost() && /^\/demo(\/|$)/.test(window.location.pathname || '');
};

const canUsePlayerPushNotifications = () => (
  typeof window !== 'undefined' &&
  'Notification' in window &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  (window.isSecureContext || isLoopbackHost())
);

const getPlayerNotificationPermission = () => {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
  return Notification.permission || 'default';
};

const getNativePlayerNotificationsEnabled = () => {
  const bridge = getNativePlayerBridge();
  if (!bridge || typeof bridge.notificationsEnabled !== 'function') return null;
  try {
    return Boolean(bridge.notificationsEnabled());
  } catch {
    return null;
  }
};

const getActivePlayerNotificationPermission = () => {
  const nativeEnabled = getNativePlayerNotificationsEnabled();
  if (nativeEnabled === true) return 'granted';
  if (nativeEnabled === false && isNativePlayerRuntime()) return 'default';
  return getPlayerNotificationPermission();
};

const wait = (ms) => new Promise(resolve => setTimeout(resolve, ms));

const urlBase64ToUint8Array = (base64String = '') => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = `${base64String}${padding}`.replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

const registerPlayerPushServiceWorker = async () => {
  if (!canUsePlayerPushNotifications()) return null;
  const existing = await navigator.serviceWorker.getRegistration('/');
  return existing || navigator.serviceWorker.register(PUSH_SW_URL, { scope: '/' });
};

const enablePlayerPushNotifications = async () => {
  if (!canUsePlayerPushNotifications()) return { ok: false, status: 'unsupported' };

  let permission = getPlayerNotificationPermission();
  if (permission === 'default') {
    permission = await Notification.requestPermission();
  }
  if (permission !== 'granted') {
    return { ok: false, status: permission || 'denied' };
  }

  const registration = await registerPlayerPushServiceWorker();
  if (!registration?.pushManager) return { ok: false, status: 'unsupported' };

  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    let data;
    try {
      ({ data } = await api.get('/api/push/vapid-key'));
    } catch {
      return { ok: false, status: 'sync-unavailable' };
    }
    const publicKey = String(data?.public_key || '').trim();
    if (!publicKey) return { ok: false, status: 'missing-key' };
    try {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
    } catch {
      return { ok: false, status: 'subscribe-failed' };
    }
  }

  const json = subscription.toJSON();
  try {
    await api.post('/api/push/subscribe', {
      endpoint: json.endpoint,
      keys: json.keys,
    });
  } catch {
    return { ok: false, status: 'save-failed' };
  }

  return { ok: true, status: 'granted' };
};

const registerNativePlayerFcmToken = async (token, context = {}) => {
  const cleanToken = String(token || '').trim();
  if (!cleanToken) return false;
  const { tenantSlug, userId, force = false } = context;
  const storageKey = tenantSlug && userId
    ? scopedPlayerStorageKey(`${FCM_TOKEN_REGISTRATION_STORAGE_KEY}:${hashFcmToken(cleanToken)}`, tenantSlug, userId)
    : '';
  if (!force && storageKey) {
    try {
      const lastRegisteredAt = Number(localStorage.getItem(storageKey) || 0);
      if (lastRegisteredAt && Date.now() - lastRegisteredAt < FCM_TOKEN_REGISTER_MIN_INTERVAL_MS) {
        return true;
      }
    } catch { /* noop */ }
  }
  await api.post('/api/push/fcm-token', {
    token: cleanToken,
    platform: 'android',
    app: 'player-native',
  });
  if (storageKey) {
    try { localStorage.setItem(storageKey, String(Date.now())); } catch { /* noop */ }
  }
  return true;
};

const setBrowserAppBadge = (count) => {
  if (typeof navigator === 'undefined') return;
  const nextCount = Math.max(0, Math.min(99, Number(count) || 0));
  try {
    if (nextCount > 0 && typeof navigator.setAppBadge === 'function') {
      void navigator.setAppBadge(nextCount);
    } else if (nextCount <= 0 && typeof navigator.clearAppBadge === 'function') {
      void navigator.clearAppBadge();
    }
  } catch { /* noop */ }

  try {
    navigator.serviceWorker?.controller?.postMessage({
      type: nextCount > 0 ? 'SET_APP_BADGE' : 'CLEAR_APP_BADGE',
      count: nextCount,
    });
  } catch { /* noop */ }
};

const chatAgentKey = (agent) => {
  const id = String(agent?.id || '').trim().toLowerCase();
  if (id) return `id:${id}`;
  const name = String(agent?.name || '').trim().toLowerCase();
  return name ? `name:${name}` : '';
};

const readStoredArray = (storage, key) => {
  try {
    if (!storage || !key) return [];
    const parsed = JSON.parse(storage.getItem(key) || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const freshGet = (url, scope) => api.get(url, {
  timeout: 20000,
  params: {
    _scope: scope || 'player',
    _ts: Date.now(),
  },
  headers: {
    'Cache-Control': 'no-cache',
    Pragma: 'no-cache',
  },
});

const finiteNumberOr = (value, fallback = 0) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
};

const paymentMethodDisplayCode = (method) => String(
  method?.gateway_method_code
  || method?.gateway_method
  || method?.method
  || method?.code
  || method?.title
  || ''
).toLowerCase().replace(/[^a-z0-9]+/g, '');

const defaultPaymentMethodOrder = (method) => {
  const code = paymentMethodDisplayCode(method);
  if (code === 'cashappvialt' || code === 'cashapplt' || code === 'ecashapp') return 15;
  if (code === 'cashappqr') return 1;
  if (code === 'cashapp') return 10;
  if (code === 'applepay') return 20;
  if (code === 'googlepay') return 30;
  if (code === 'chime') return 40;
  if (code === 'venmo') return 50;
  if (code === 'crypto' || code === 'usdt' || code === 'btc' || code === 'bitcoin') return 60;
  return 500;
};

const paymentMethodOrder = (method) => {
  const raw = Number(method?.display_order ?? method?.sort_order ?? method?.order);
  return Number.isFinite(raw) ? raw : defaultPaymentMethodOrder(method);
};

const shouldShowNotificationsNudge = ({
  nativeStorageKey,
  permission,
  webStorageKey,
}) => {
  const nativeRuntime = isNativePlayerRuntime();
  if (permission !== 'granted') return true;
  if (nativeRuntime) return getNativePlayerNotificationsEnabled() !== true;

  try {
    return !localStorage.getItem(webStorageKey || nativeStorageKey);
  } catch {
    return true;
  }
};

function AllowNotificationsNudge({
  tenantSlug,
  userId,
  homeNudgeIndex,
  homeNudgePhase = 'visible',
  homeNudgeExitDuration = 1400,
  onHomeNudgeVisibilityChange,
}) {
  const webStorageKey = scopedPlayerStorageKey(PUSH_NOTIFICATIONS_STORAGE_KEY, tenantSlug, userId);
  const nativeStorageKey = scopedPlayerStorageKey(NATIVE_PUSH_NOTIFICATIONS_STORAGE_KEY, tenantSlug, userId);
  const [permission, setPermission] = useState(() => getActivePlayerNotificationPermission());
  const [visible, setVisible] = useState(() => shouldShowNotificationsNudge({
    nativeStorageKey,
    permission,
    webStorageKey,
  }));
  const [busy, setBusy] = useState(false);
  const [syncStatus, setSyncStatus] = useState('');

  useEffect(() => {
    const markSynced = () => {
      try {
        const now = String(Date.now());
        localStorage.setItem(nativeStorageKey, now);
        localStorage.setItem(webStorageKey, now);
      } catch { /* noop */ }
    };

    const refresh = () => {
      const nativeRuntime = isNativePlayerRuntime();
      const nextPermission = getActivePlayerNotificationPermission();
      setPermission(nextPermission);
      if (nextPermission === 'granted') {
        setSyncStatus((status) => status || 'allowed');
        if (nativeRuntime) {
          markSynced();
          setVisible(false);
          return;
        }
      }
      const synced = (() => {
        try {
          return Boolean(localStorage.getItem(nativeRuntime ? nativeStorageKey : webStorageKey));
        } catch { return false; }
      })();
      const notificationReady = nextPermission === 'granted'
        && (nativeRuntime ? getNativePlayerNotificationsEnabled() === true : synced);
      setVisible(!notificationReady);
    };

    refresh();
    const onReady = () => {
      setPermission('granted');
      markSynced();
      setVisible(false);
    };
    window.addEventListener('vl:push-enabled', onReady);
    window.addEventListener('teamlobby-fcm-token', refresh);
    window.addEventListener('teamlobby:fcm-token', refresh);
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.removeEventListener('vl:push-enabled', onReady);
      window.removeEventListener('teamlobby-fcm-token', refresh);
      window.removeEventListener('teamlobby:fcm-token', refresh);
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [nativeStorageKey, webStorageKey]);

  useEffect(() => {
    onHomeNudgeVisibilityChange?.(homeNudgeIndex, visible);
  }, [homeNudgeIndex, onHomeNudgeVisibilityChange, visible]);

  if (!visible) return null;

  const handleAllow = async () => {
    tap();
    setBusy(true);
    try {
      const nativeBridge = getNativePlayerBridge();
      const finishLocalSync = (description = 'App alerts are ready for this device.') => {
        try {
          const now = String(Date.now());
          localStorage.setItem(nativeBridge ? nativeStorageKey : webStorageKey, now);
          if (nativeBridge) localStorage.setItem(webStorageKey, now);
        } catch { /* noop */ }
        try { window.dispatchEvent(new Event('vl:push-enabled')); } catch { /* noop */ }
        setPermission('granted');
        setVisible(false);
        toast.success('Notifications enabled', {
          description,
          duration: 2400,
        });
      };

      if (nativeBridge) {
        try { nativeBridge.requestNotifications?.(); } catch { /* noop */ }
        try { nativeBridge.refreshFcmToken?.(); } catch { /* noop */ }

        let nativeEnabled = getNativePlayerNotificationsEnabled() === true;
        for (const delay of [250, 600, 1000, 1700]) {
          if (nativeEnabled) break;
          await wait(delay);
          nativeEnabled = getNativePlayerNotificationsEnabled() === true;
        }

        try {
          const token = nativeBridge.getFcmToken?.();
          if (token) await registerNativePlayerFcmToken(token, { tenantSlug, userId });
        } catch {
          // Native token retry continues from the Android wrapper.
        }

        if (nativeEnabled) {
          finishLocalSync('Android app alerts and badge counts are ready.');
          return;
        }

        setPermission('default');
        setVisible(true);
        toast.error('Notifications still need approval', {
          description: 'Tap Allow in the Android permission popup, then this card will disappear.',
          duration: 3400,
        });
        return;
      }

      const result = await enablePlayerPushNotifications();
      const nextPermission = getPlayerNotificationPermission();
      setPermission(nextPermission);
      if (result.ok) {
        finishLocalSync('App alerts and badge counts are ready for this device.');
        return;
      }

      if (result.status === 'denied') {
        setPermission('denied');
        toast.error('Still blocked', {
          description: 'Open browser site settings, set Notifications to Allow, then tap Sync.',
          duration: 3200,
        });
        return;
      }
      if (result.status === 'missing-key') {
        if (nextPermission === 'granted' && isLocalDemoPreview()) {
          finishLocalSync('Permission is on for local demo. Live will save the device subscription.');
          return;
        }
        setSyncStatus('setup-missing');
        toast.error('Notification sync not ready', {
          description: 'The push key is missing on this environment. Try Sync on the live app.',
          duration: 3000,
        });
        return;
      }
      if (result.status === 'unsupported') {
        setSyncStatus('unsupported');
        toast.error('Notifications are not supported here', {
          description: 'Open in Chrome or the installed app, then tap Allow again.',
          duration: 3200,
        });
        return;
      }
      if (['sync-unavailable', 'save-failed'].includes(result.status)) {
        if (nextPermission === 'granted' && isLocalDemoPreview()) {
          finishLocalSync('Permission is on for local demo. Live will save the device subscription.');
          return;
        }
        setSyncStatus('sync-failed');
        toast.error('Permission allowed, sync not finished', {
          description: 'This device could not be saved yet. Tap Sync again when the app is online.',
          duration: 3600,
        });
        return;
      }
      if (result.status === 'subscribe-failed') {
        if (nextPermission === 'granted' && isLocalDemoPreview()) {
          finishLocalSync('Permission is on for local demo. Live will save the device subscription.');
          return;
        }
        setSyncStatus('subscribe-failed');
        toast.error('Permission allowed, sync not finished', {
          description: 'Reload or install the app, then tap Sync again.',
          duration: 3600,
        });
        return;
      }
      toast.error('Could not enable notifications', {
        description: 'Try again after installing the app or reloading.',
        duration: 3000,
      });
    } catch {
      setSyncStatus('sync-failed');
      toast.error('Could not enable notifications', {
        description: 'Permission may be allowed, but device sync did not finish. Tap Sync again.',
        duration: 3000,
      });
    } finally {
      setBusy(false);
    }
  };

  const isBlocked = permission === 'denied';
  const isAllowedPending = permission === 'granted';
  const ctaLabel = busy ? 'Checking' : isBlocked || isAllowedPending ? 'Sync' : 'Allow';
  const bodyCopy = isBlocked
    ? 'Allow notifications in browser settings, then tap Sync.'
    : isAllowedPending
      ? syncStatus === 'sync-failed' || syncStatus === 'setup-missing' || syncStatus === 'subscribe-failed'
        ? 'Permission is on. Tap Sync to retry.'
        : 'Permission is on. Tap Sync to finish.'
      : 'Bonus, payment and chat alerts in your app.';

  return (
    <PlayerActionNudge
      variant="notifications"
      title="Notifications"
      description={bodyCopy}
      status={isBlocked ? 'Blocked in browser' : isAllowedPending ? 'Sync needed' : null}
      actionLabel={ctaLabel}
      onAction={handleAllow}
      busy={busy}
      phase={homeNudgePhase}
      exitDuration={homeNudgeExitDuration}
      testId="allow-notifications-nudge"
    />
  );
}

const isAndroidBrowser = () => {
  if (typeof navigator === 'undefined') return false;
  return /android/i.test(navigator.userAgent || '');
};

function InstallAppNudge({
  brandLogo,
  brandName,
  tenantSlug,
  onOpenAndroidApp,
  onOpenShortcutInstall,
  homeNudgeIndex,
  homeNudgePhase = 'visible',
  homeNudgeExitDuration = 1400,
  onHomeNudgeVisibilityChange,
}) {
  const apkUrl = useMemo(() => playerApkPath(tenantSlug), [tenantSlug]);
  const canDownloadApk = Boolean(apkUrl) && isAndroidBrowser() && !isNativePlayerRuntime();
  const [visible, setVisible] = useState(() => canDownloadApk || canOfferInstall());

  useEffect(() => {
    const refresh = () => setVisible(canDownloadApk || canOfferInstall());
    refresh();

    window.addEventListener('appinstalled', refresh);
    window.addEventListener('vl:pwa-installed', refresh);
    const displayMode = window.matchMedia?.('(display-mode: standalone)');
    displayMode?.addEventListener?.('change', refresh);
    displayMode?.addListener?.(refresh);

    return () => {
      window.removeEventListener('appinstalled', refresh);
      window.removeEventListener('vl:pwa-installed', refresh);
      displayMode?.removeEventListener?.('change', refresh);
      displayMode?.removeListener?.(refresh);
    };
  }, [canDownloadApk]);

  useEffect(() => {
    onHomeNudgeVisibilityChange?.(homeNudgeIndex, visible);
  }, [homeNudgeIndex, onHomeNudgeVisibilityChange, visible]);

  if (!visible) return null;

  const handleInstall = async () => {
    tap();
    if (canDownloadApk) {
      onOpenAndroidApp?.();
      return;
    }
    if (onOpenShortcutInstall) {
      await onOpenShortcutInstall();
      return;
    }
    const opened = await openInstallPrompt();
    if (!opened) {
      toast('Install app from your browser menu', {
        description: 'Use the install icon beside the address bar or the browser menu.',
        duration: 2600,
      });
    }
  };

  return (
    <PlayerActionNudge
      variant="install"
      title={canDownloadApk ? 'Get the Android app' : 'Install the mobile app'}
      description={canDownloadApk
        ? 'Games, wallet and chat. One tap away.'
        : 'Add your team’s lobby to your home screen.'}
      brandLogo={brandLogo}
      actionLabel={canDownloadApk ? 'Download' : 'Install'}
      onAction={handleInstall}
      phase={homeNudgePhase}
      exitDuration={homeNudgeExitDuration}
      testId="install-app-nudge"
    />
  );
}

const sortPlayerPaymentMethods = (items = []) => [...items].sort((a, b) => {
  const orderDiff = paymentMethodOrder(a) - paymentMethodOrder(b);
  if (orderDiff !== 0) return orderDiff;
  return String(a?.title || a?.method || '').localeCompare(String(b?.title || b?.method || ''));
});

const accountSyncTime = (account) => {
  const value = account?.last_balance_synced_at || account?.last_synced_at || account?.updated_at || account?.created_at;
  const time = Date.parse(value || '');
  return Number.isFinite(time) ? time : 0;
};

const sameAccount = (a, b) => {
  if (!a || !b) return false;
  if (a.id && b.id && String(a.id) === String(b.id)) return true;
  if (a.game_id && b.game_id && String(a.game_id) === String(b.game_id)) return true;
  if (a.game_code && b.game_code && String(a.game_code) === String(b.game_code)) return true;
  return false;
};

const mergeLiveBalanceRows = (accounts, rows) => {
  const liveRows = Array.isArray(rows) ? rows : [];
  if (!liveRows.length) return Array.isArray(accounts) ? accounts : [];
  return (Array.isArray(accounts) ? accounts : []).map(account => {
    const row = liveRows.find(item => sameAccount(account, item));
    if (!row) return account;
    const rowTime = accountSyncTime(row);
    const currentTime = accountSyncTime(account);
    if (rowTime && currentTime && rowTime < currentTime) return account;
    const liveBalance = finiteNumberOr(row.balance_usd ?? row.last_known_balance ?? row.balance, account.last_known_balance ?? account.balance_usd ?? 0);
    const syncedAt = row.last_balance_synced_at || row.last_synced_at || new Date().toISOString();
    return {
      ...account,
      last_known_balance: liveBalance,
      balance_usd: liveBalance,
      balance_sc: finiteNumberOr(row.balance_sc, liveBalance),
      provider_status: row.provider_status || account.provider_status,
      is_cached: Boolean(row.is_cached),
      last_balance_synced_at: syncedAt,
      last_load_amount: row.last_load_amount ?? account.last_load_amount,
      last_load_fund_source: row.last_load_fund_source ?? account.last_load_fund_source,
      last_load_at: row.last_load_at ?? account.last_load_at,
      last_redeem_at: row.last_redeem_at ?? account.last_redeem_at,
    };
  });
};

const mergeServerAccountsPreservingLive = (existingAccounts, serverAccounts) => {
  const existing = Array.isArray(existingAccounts) ? existingAccounts : [];
  const incoming = Array.isArray(serverAccounts) ? serverAccounts : [];
  const merged = existing.flatMap(current => {
    const account = incoming.find(item => sameAccount(item, current));
    if (!account) {
      // A successful server response is authoritative for saved accounts.
      // Keep only an in-flight placeholder; a formerly successful local pin
      // must disappear when an admin deletes its server record.
      return current.__creating ? [current] : [];
    }
    const currentTime = accountSyncTime(current);
    const incomingTime = accountSyncTime(account);
    if (currentTime > incomingTime) {
      return [{
        ...current,
        ...account,
        last_known_balance: current.last_known_balance ?? current.balance_usd ?? account.last_known_balance,
        balance_usd: current.balance_usd ?? current.last_known_balance ?? account.balance_usd,
        balance_sc: current.balance_sc ?? account.balance_sc,
        provider_status: current.provider_status || account.provider_status,
        is_cached: current.is_cached ?? account.is_cached,
        last_balance_synced_at: current.last_balance_synced_at || account.last_balance_synced_at,
      }];
    }
    return [{
      ...current,
      ...account,
      __localOptimistic: current.__localOptimistic || account.__localOptimistic,
      __localPinnedAt: current.__localPinnedAt || account.__localPinnedAt,
    }];
  });

  const newIncoming = incoming.filter(account => (
    !existing.some(item => sameAccount(item, account))
  ));

  const stillPending = existing.filter(account => (
    account.__creating &&
    !merged.some(item => sameAccount(item, account))
  ));
  return [...stillPending, ...merged, ...newIncoming];
};

const compactAlertTime = (value) => {
  const time = Date.parse(value || '');
  if (!Number.isFinite(time)) return '';
  const diffMs = Date.now() - time;
  const minute = 60 * 1000;
  const hour = 60 * minute;
  if (diffMs < minute) return 'now';
  if (diffMs < hour) return `${Math.max(1, Math.floor(diffMs / minute))}m ago`;
  if (diffMs < 24 * hour) return `${Math.floor(diffMs / hour)}h ago`;
  return new Date(time).toLocaleDateString([], { month: 'short', day: 'numeric' });
};

const isNotificationUnread = (item) => !item?.read && !item?.is_read;

function HeaderAlertsButton({
  unread,
  open,
  onOpenChange,
  loading,
  items,
  onItemClick,
  onMarkAll,
  onViewAll,
}) {
  const latest = (items || []).slice(0, 5);
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="player-header-alerts tap-pop-alert relative grid h-12 w-12 shrink-0 place-items-center rounded-full transition active:scale-95 hover:brightness-110"
          style={{
            background: 'radial-gradient(circle at 32% 24%, rgba(255,255,255,0.30) 0%, rgba(255,217,61,0.22) 32%, rgba(168,85,247,0.16) 72%, rgba(10,18,26,0.86) 100%)',
            border: '1px solid rgba(255,217,61,0.42)',
            boxShadow: '0 10px 24px rgba(0,0,0,0.30), 0 0 18px rgba(255,217,61,0.22), inset 0 0 10px rgba(255,255,255,0.16)',
          }}
          aria-label="Open alerts"
          data-testid="header-alerts-button"
        >
          <Bell className="h-5 w-5 text-white" strokeWidth={2.6} />
          {unread > 0 && (
            <span
              className="absolute -bottom-1.5 -left-1.5 min-w-[22px] rounded-full px-1.5 py-0.5 text-center text-[10px] font-black leading-none text-white"
              style={{
                background: '#FF3D71',
                border: '2px solid var(--vl-bg, #000)',
                boxShadow: '0 0 14px rgba(255,61,113,0.76)',
              }}
              data-testid="header-alerts-badge"
            >
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={12}
        className="overflow-hidden rounded-3xl border p-0 shadow-2xl"
        style={{
          width: 'min(330px, calc(100vw - 24px))',
          background: 'linear-gradient(180deg, rgba(15,21,32,0.98), rgba(5,9,14,0.98))',
          borderColor: 'rgba(255,90,122,0.35)',
          color: '#fff',
          boxShadow: '0 22px 60px rgba(0,0,0,0.55), 0 0 34px rgba(255,61,113,0.15)',
        }}
        data-testid="header-alerts-popover"
      >
        <div className="flex items-center justify-between gap-3 border-b border-white/10 px-4 py-3">
          <div>
            <p className="display text-sm font-extrabold text-white">Alerts</p>
            <p className="text-[11px] text-white/55">Latest 5 updates</p>
          </div>
          {unread > 0 && (
            <button
              type="button"
              onClick={onMarkAll}
              className="rounded-full px-3 py-1 text-[11px] font-bold text-white"
              style={{ background: 'rgba(255,61,113,0.22)', border: '1px solid rgba(255,61,113,0.42)' }}
              data-testid="header-alerts-mark-all"
            >
              Mark read
            </button>
          )}
        </div>
        <div className="max-h-[360px] overflow-y-auto p-2">
          {loading && (
            <div className="py-8 text-center text-sm text-white/60">Loading alerts...</div>
          )}
          {!loading && latest.length === 0 && (
            <div className="py-8 text-center">
              <Bell className="mx-auto mb-2 h-8 w-8 text-white/40" />
              <p className="text-sm font-bold text-white/75">No alerts yet</p>
            </div>
          )}
          {!loading && latest.map(item => {
            const unreadItem = isNotificationUnread(item);
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => onItemClick(item)}
                className="w-full rounded-2xl px-3 py-2.5 text-left transition active:scale-[0.99] hover:bg-white/5"
                style={{
                  background: unreadItem ? 'rgba(255,61,113,0.12)' : 'rgba(255,255,255,0.035)',
                  border: `1px solid ${unreadItem ? 'rgba(255,61,113,0.32)' : 'rgba(255,255,255,0.07)'}`,
                }}
                data-testid={`header-alert-${item.id}`}
              >
                <div className="flex items-start gap-2">
                  <Bell className="mt-0.5 h-4 w-4 shrink-0" style={{ color: unreadItem ? '#FF5A7A' : 'rgba(255,255,255,0.42)' }} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <p className="truncate text-[13px] font-extrabold text-white">{item.title || 'Notification'}</p>
                      <span className="shrink-0 text-[10px] text-white/42">{compactAlertTime(item.created_at)}</span>
                    </div>
                    {item.message && (
                      <p className="mt-0.5 line-clamp-2 text-[11px] leading-snug text-white/62">{item.message}</p>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
        <button
          type="button"
          onClick={onViewAll}
          className="w-full border-t border-white/10 px-4 py-3 text-center text-xs font-extrabold text-white transition hover:bg-white/5"
          data-testid="header-alerts-view-all"
        >
          Open all alerts
        </button>
      </PopoverContent>
    </Popover>
  );
}

export function RequiredNameOnboarding({ user, branding, onComplete }) {
  const { updateProfile } = useAuth();
  const brandName = branding?.brand_name || user?.tenant_name || 'our lobby';
  const brandLogo = branding?.logo_url || branding?.intro_logo_url || '';
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const submitting = useRef(false);
  const [saveError, setSaveError] = useState('');
  const cleanName = name.trim().replace(/\s+/g, ' ');
  const canSave = cleanName.length >= 2;

  const submit = async (event) => {
    event?.preventDefault?.();
    if (!canSave || submitting.current) return;
    submitting.current = true;
    setSaving(true);
    setSaveError('');
    try {
      await updateProfile({ full_name: cleanName });
      toast.success('Name saved');
      onComplete?.();
    } catch (error) {
      if (error?.response?.status === 401) {
        toast.error('Please sign in again to finish setting your name.');
      } else {
        const detail = error?.response?.data?.detail;
        setSaveError(error?.code === 'SESSION_CHANGED' ? 'Your session changed. Check your account and enter your name again.' : (typeof detail === 'string' ? detail : 'Could not save your name. Please try again.'));
      }
    } finally {
      submitting.current = false;
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center overflow-y-auto overscroll-contain px-3 py-3 sm:px-4 sm:py-5"
      style={{
        background: 'rgba(3, 8, 14, 0.68)',
        backdropFilter: 'blur(18px)',
        WebkitBackdropFilter: 'blur(18px)',
      }}
      data-testid="required-name-onboarding"
    >
      <form
        onSubmit={submit}
        aria-busy={saving}
        className="max-h-[calc(100dvh-1.5rem)] w-full max-w-md overflow-y-auto overscroll-contain rounded-[30px] p-4 sm:max-h-[calc(100dvh-2.5rem)] sm:p-6"
        style={{
          background: 'linear-gradient(180deg, color-mix(in oklab, var(--vl-bg, #07131F) 92%, #FFFFFF), var(--vl-bg, #07131F))',
          border: `1px solid ${PLAYER_UI_ACCENT_BORDER}`,
          boxShadow: `0 22px 70px rgba(0,0,0,0.62), 0 0 44px ${PLAYER_UI_ACCENT_GLOW}`,
          color: '#FFFFFF',
        }}
      >
        <div className="flex flex-col items-center text-center">
          <div
            className="mb-4 flex h-20 w-20 items-center justify-center overflow-hidden rounded-full"
            style={{
              background: 'rgba(255,255,255,0.06)',
              border: '2px solid color-mix(in oklab, var(--vl-profile-accent, var(--vl-neon, #22FF88)) 58%, #FFFFFF 12%)',
              boxShadow: `0 0 24px ${PLAYER_UI_ACCENT_GLOW}`,
            }}
          >
            {brandLogo ? (
              <SafeImage src={brandLogo} alt={brandName} className="h-full w-full object-cover" fallback={<CircleUser className="h-9 w-9 text-white/80" />} />
            ) : (
              <CircleUser className="h-9 w-9 text-white/80" />
            )}
          </div>
          <p className="text-xs font-extrabold uppercase tracking-[0.32em]" style={{ color: 'var(--vl-neon, #22FF88)' }}>
            Welcome to
          </p>
          <h2 className="display mt-1 max-w-full break-words text-2xl font-extrabold leading-tight">{brandName}</h2>
          <p className="mt-3 text-sm leading-relaxed text-white/70">
            Enter your full name to continue your journey.
          </p>
        </div>

        <div className="mt-5">
          <label className="text-[11px] font-bold uppercase tracking-[0.22em] text-white/55" htmlFor="required-full-name">
            Full name
          </label>
          <Input
            id="required-full-name"
            autoComplete="name"
            aria-describedby={saveError ? 'required-name-error' : undefined}
            value={name}
            disabled={saving}
            onChange={(event) => setName(event.target.value)}
            autoFocus={typeof window !== 'undefined' && window.matchMedia?.('(min-width: 640px)').matches}
            maxLength={40}
            placeholder="Enter your full name"
            className="mt-2 h-14 rounded-2xl text-base font-bold"
            style={{
              background: 'rgba(0,0,0,0.42)',
              borderColor: canSave ? 'var(--vl-neon, #22FF88)' : 'rgba(255,255,255,0.16)',
              color: '#FFFFFF',
              boxShadow: canSave ? `0 0 18px ${PLAYER_UI_ACCENT_GLOW}` : 'none',
            }}
            data-testid="required-full-name-input"
          />
        </div>

        {saveError && <p id="required-name-error" role="alert" className="mt-3 text-sm text-red-200">{saveError}</p>}

        <Button
          type="submit"
          disabled={!canSave || saving}
          className="mt-5 h-13 w-full rounded-2xl display text-base font-extrabold"
          style={{
            background: canSave
              ? 'linear-gradient(135deg, var(--vl-btn-primary-from, var(--vl-neon, #22FF88)), var(--vl-btn-primary-to, #00C853))'
              : 'rgba(255,255,255,0.12)',
            color: canSave ? 'var(--vl-btn-primary-text, #03130A)' : 'rgba(255,255,255,0.48)',
            boxShadow: canSave ? `0 12px 28px ${PLAYER_UI_ACCENT_GLOW}` : 'none',
          }}
          data-testid="required-full-name-save"
        >
          {saving ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : <Check className="mr-2 h-4 w-4" />}
          Continue
        </Button>
      </form>
    </div>
  );
}

export default function PlayerApp() {
  const { user, logout, checkAuth, tenantSlug: authTenantSlug } = useAuth();
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const tenantSlug = params.tenantSlug || authTenantSlug || null;
  const playerSessionScope = user?.id ? `${tenantSlug || 'global'}:${user.id}` : '';
  const lobbyKey = playerLobbyKey(user);
  const initialLobby = useRef(readPlayerLobby(lobbyKey)).current;
  const recoveryLoading = useRef(playerRecoveryPending()).current;
  const lobbyRevisionRef = useRef(playerSessionRevision());
  const readyLobbyKeyRef = useRef(initialLobby ? lobbyKey : '');
  const [announcementCycle, setAnnouncementCycle] = useState(!initialLobby);
  const [loading, setLoading] = useState(!initialLobby);
  const [playerDataReadyScope, setPlayerDataReadyScope] = useState(initialLobby ? playerSessionScope : '');
  const balanceSyncInFlightRef = useRef(false);
  const lastBalanceSyncAtRef = useRef(0);
  const walletSyncInFlightRef = useRef(false);
  const [data, setData] = useState(() => initialLobby?.data || emptyPlayerData());
  const [wallet, setWallet] = useState(() => initialLobby?.wallet || { balance: 0, pending: 0, play_credits: 0 });
  const [walletRules, setWalletRules] = useState(() => initialLobby?.walletRules || { withdrawal_rules: { min_amount: 20, max_amount: 5000, max_total_per_day: 0 } });
  const [badge, setBadge] = useState({ phone_verified: false, show_badge: false, tier: { key: 'none', color: '#3B82F6', label: 'Member' } });
  const [chatUnread, setChatUnread] = useState(0);
  const [chatAgentUnread, setChatAgentUnread] = useState([]);
  const [branding, setBranding] = useState(() => initialLobby?.branding || { logo_url: '', brand_name: '', brand_tagline: 'Player Lounge' });
  // Pull the active tenant's custom theme — its brand_name / logo_url /
  // tagline override the platform branding when set, so a tenant's
  // splash + header instantly reflect their identity.
  const { theme: tenantTheme, tenantId: themeTenantId } = useTenantTheme();
  const themeMatchesUser = !user?.tenant_id || themeTenantId === user.tenant_id;
  const effectiveBranding = useMemo(() => ({
    logo_url: (themeMatchesUser && tenantTheme.logo_url?.trim()) || branding.logo_url || '',
    brand_name: (themeMatchesUser && tenantTheme.brand_name?.trim()) || user?.tenant_name?.trim() || (!user?.tenant_id && branding.brand_name) || '',
    brand_tagline: (tenantTheme.tagline && tenantTheme.tagline.trim()) || branding.brand_tagline || 'Player Lounge',
    intro_logo_url: (themeMatchesUser && tenantTheme.intro_logo_url?.trim()) || '',
    description: branding.description || '',
    verified: branding.verified,
    stats: branding.stats || {},
    socials: branding.socials || {},
    social_visibility: branding.social_visibility || {},
    support: branding.support || {},
    support_visibility: branding.support_visibility || {},
    support_icons: branding.support_icons || {},
    chat_agents: branding.chat_agents || [],
  }), [tenantTheme, branding, themeMatchesUser, user?.tenant_name]);
  const activeTenantSlug = tenantSlug || user?.tenant_slug || null;
  const tenantApkUrl = useMemo(() => playerApkPath(activeTenantSlug), [activeTenantSlug]);
  const canDownloadTenantApk = Boolean(tenantApkUrl) && isAndroidBrowser() && !isNativePlayerRuntime();
  const openAndroidAppDownloadPage = () => {
    tap();
    navigate(params.tenantSlug ? `/${params.tenantSlug}/download-app` : '/download-app');
  };
  const openShortcutInstallHelper = async () => {
    tap();
    const opened = await openInstallPrompt();
    if (!opened) {
      toast('Add this site to your home screen', {
        description: 'Use your browser install icon or menu, then choose Install app or Add to Home Screen.',
        duration: 3000,
      });
    }
  };
  const fullNameLooksAutoGenerated = Boolean(
    user &&
    user.profile_name_required !== false &&
    String(user.full_name || '').trim().length <= 1
  );
  const requiresNameOnboarding = Boolean(
    user &&
    user.role === 'user' &&
    (user.profile_name_required === true || fullNameLooksAutoGenerated)
  );

  const [walletOpen, setWalletOpen] = useState(false);
  const [walletMode, setWalletMode] = useState('deposit');
  const [cashoutStage, setCashoutStage] = useState('choice');
  const [cashoutSpeed, setCashoutSpeed] = useState('normal');
  const [walletStep, setWalletStep] = useState(1); // 1 = method, 2 = amount
  const [walletMethod, setWalletMethod] = useState(null);
  const [supportMenuOpen, setSupportMenuOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [selectedChatAgent, setSelectedChatAgent] = useState(null);
  const [openCommunityFromSupport, setOpenCommunityFromSupport] = useState(false);
  const [addGameOpen, setAddGameOpen] = useState(false);
  // Lifted game-creation state — survives dialog close so a request keeps
  // running in the background, and the player can launch multiple games
  // in parallel. Shape: { [gameId]: { status: 'creating'|'success'|'error', game, error?, account? } }
  const [gameCreations, setGameCreations] = useState({});
  const [resetPwOpen, setResetPwOpen] = useState(false);
  const [editProfileOpen, setEditProfileOpen] = useState(false);
  const [editProfileMode, setEditProfileMode] = useState(null);
  const [phoneVerificationBackSignal, setPhoneVerificationBackSignal] = useState(0);
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileIdentityFocusKey, setProfileIdentityFocusKey] = useState(0);
  const [balanceDetailOpen, setBalanceDetailOpen] = useState(false);
  const [announcementsOpen, setAnnouncementsOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notificationDetailOpen, setNotificationDetailOpen] = useState(false);
  const [notificationDetailItem, setNotificationDetailItem] = useState(null);
  const [achievementsOpen, setAchievementsOpen] = useState(false);
  const [notifUnread, setNotifUnread] = useState(0);
  const [homeAlertsOpen, setHomeAlertsOpen] = useState(false);
  const [homeAlertsLoading, setHomeAlertsLoading] = useState(false);
  const [homeAlertItems, setHomeAlertItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const brandingScope = `branding:${tenantSlug || 'global'}`;
  const balanceCoachStorageKey = user?.id ? `vl_balance_coach_seen:${tenantSlug || 'global'}:${user.id}` : '';
  const pendingGamesStorageKey = scopedPlayerStorageKey(PENDING_GAMES_STORAGE_KEY, tenantSlug, user?.id);
  const favoritesStorageKey = scopedPlayerStorageKey(FAVORITES_STORAGE_KEY, tenantSlug, user?.id);
  const [favorites, setFavorites] = useState(() => new Set());
  const [balanceCoachVisible, setBalanceCoachVisible] = useState(false);
  const [focusGameAccountId, setFocusGameAccountId] = useState(null);
  const [pendingGameAccounts, setPendingGameAccounts] = useState([]);
  const previousChatUnreadRef = useRef(null);
  const activePlayerScopeRef = useRef('');
  const activeBrandingScopeRef = useRef('');
  const brandingRefreshInFlightRef = useRef(false);
  const suppressOverlayHistoryRef = useRef(false);
  const overlayStackRef = useRef([]);
  const overlayRestoreRef = useRef({});
  const overlayReleaseTimerRef = useRef(null);
  const historyGuardPushTimerRef = useRef(null);
  const gameCreationTimersRef = useRef({});

  useEffect(() => {
    if (!user?.id || typeof window === 'undefined') return undefined;
    const nativeApp = isNativePlayerRuntime();
    const installedPwa = Boolean(
      window.matchMedia?.('(display-mode: standalone)').matches
      || window.navigator?.standalone === true,
    );
    if (!nativeApp && !installedPwa) return undefined;

    const storageKey = scopedPlayerStorageKey(
      APP_INSTALL_SIGNAL_STORAGE_KEY,
      tenantSlug,
      user.id,
    );
    try {
      if (localStorage.getItem(storageKey)) return undefined;
    } catch { /* Retry on the next session when storage is unavailable. */ }

    let cancelled = false;
    api.post('/api/pwa/install-event', {
      outcome: 'appinstalled',
      platform_hint: nativeApp ? 'android' : undefined,
    }).then(() => {
      if (cancelled) return;
      try { localStorage.setItem(storageKey, String(Date.now())); } catch { /* noop */ }
    }).catch(() => {
      // Installation tracking must never interrupt the player app. A later
      // session retries if the player was offline during this request.
    });
    return () => { cancelled = true; };
  }, [tenantSlug, user?.id]);

  useEffect(() => {
    if (!user?.id) return undefined;
    let lastRegisteredToken = '';
    let stopped = false;
    const nativePushStorageKey = scopedPlayerStorageKey(NATIVE_PUSH_NOTIFICATIONS_STORAGE_KEY, tenantSlug, user.id);
    const webPushStorageKey = scopedPlayerStorageKey(PUSH_NOTIFICATIONS_STORAGE_KEY, tenantSlug, user.id);

    const registerToken = async (token) => {
      const cleanToken = String(token || '').trim();
      if (!cleanToken || cleanToken === lastRegisteredToken || stopped) return;
      try {
        await registerNativePlayerFcmToken(cleanToken, { tenantSlug, userId: user.id });
        lastRegisteredToken = cleanToken;
      } catch {
        // Native push registration should never interrupt the player session.
      }
    };

    const pullTokenFromBridge = () => {
      if (stopped || typeof window === 'undefined') return;
      try {
        const bridge = getNativePlayerBridge();
        if (!bridge) return;
        const notificationsEnabled = getNativePlayerNotificationsEnabled() === true;
        if (notificationsEnabled) {
          try {
            const now = String(Date.now());
            localStorage.setItem(nativePushStorageKey, now);
            localStorage.setItem(webPushStorageKey, now);
            window.dispatchEvent(new Event('vl:push-enabled'));
          } catch { /* noop */ }
        }
        bridge.refreshFcmToken?.();
        const token = bridge.getFcmToken?.();
        if (token) registerToken(token);
      } catch {
        // Keep silent; native code keeps retrying after WebView page loads.
      }
    };

    const onToken = (event) => registerToken(event?.detail?.token);
    window.addEventListener('teamlobby-fcm-token', onToken);
    window.addEventListener('teamlobby:fcm-token', onToken);
    const retryTimers = [800, 2500, 6000, 12000].map(delay => setTimeout(pullTokenFromBridge, delay));
    const interval = setInterval(pullTokenFromBridge, 45000);
    pullTokenFromBridge();

    return () => {
      stopped = true;
      window.removeEventListener('teamlobby-fcm-token', onToken);
      window.removeEventListener('teamlobby:fcm-token', onToken);
      retryTimers.forEach(clearTimeout);
      clearInterval(interval);
    };
  }, [user?.id, tenantSlug]);

  const clearGameCreationTimer = (gameId) => {
    const key = String(gameId || '');
    if (!key || !gameCreationTimersRef.current[key]) return;
    clearTimeout(gameCreationTimersRef.current[key]);
    delete gameCreationTimersRef.current[key];
  };

  const clearAllGameCreationTimers = () => {
    Object.values(gameCreationTimersRef.current || {}).forEach(timerId => clearTimeout(timerId));
    gameCreationTimersRef.current = {};
  };

  const releaseOverlayHistoryLockSoon = () => {
    if (overlayReleaseTimerRef.current) {
      clearTimeout(overlayReleaseTimerRef.current);
    }
    overlayReleaseTimerRef.current = setTimeout(() => {
      suppressOverlayHistoryRef.current = false;
    }, 0);
  };

  const withOverlayHistorySuppressed = (fn) => {
    suppressOverlayHistoryRef.current = true;
    fn?.();
    releaseOverlayHistoryLockSoon();
  };

  const playerHomePath = tenantSlug ? `/${tenantSlug}/app` : '/app';

  const fetchBranding = async ({ scope = brandingScope } = {}) => {
    const requestScope = scope || brandingScope;
    if (brandingRefreshInFlightRef.current) return null;
    brandingRefreshInFlightRef.current = true;
    try {
      const res = await freshGet('/api/branding', requestScope);
      if (activeBrandingScopeRef.current && activeBrandingScopeRef.current !== requestScope) return null;
      const nextBranding = res.data || {};
      // A removed brand field must not survive from an earlier refresh/tenant.
      setBranding({ logo_url: '', brand_name: '', brand_tagline: 'Player Lounge', ...nextBranding });
      return nextBranding;
    } catch {
      return null;
    } finally {
      brandingRefreshInFlightRef.current = false;
    }
  };

  const pushPlayerHomeGuardBuffer = () => {
    if (typeof window === 'undefined') return;
    try {
      for (let i = 0; i < PLAYER_HISTORY_GUARD_DEPTH; i += 1) {
        window.history.pushState({
          playerHome: true,
          playerGuard: true,
          playerGuardSlot: i,
          playerNonce: `home-${Date.now()}-${i}`,
        }, '', playerHomePath);
      }
    } catch {
      // ignore history push failures
    }
  };

  const schedulePlayerHomeGuardBuffer = () => {
    if (typeof window === 'undefined') return;
    if (historyGuardPushTimerRef.current) {
      clearTimeout(historyGuardPushTimerRef.current);
    }
    historyGuardPushTimerRef.current = setTimeout(() => {
      pushPlayerHomeGuardBuffer();
      historyGuardPushTimerRef.current = null;
    }, 0);
  };

  const parkPlayerHistoryAtHome = ({ resetStack = false } = {}) => {
    if (typeof window === 'undefined') return;
    try {
      if (resetStack) overlayStackRef.current = [];
      const rootState = {
        playerRoot: true,
        playerGuard: true,
        playerNonce: `root-${Date.now()}`,
      };
      window.history.replaceState(rootState, '', playerHomePath);
      pushPlayerHomeGuardBuffer();
    } catch {
      // ignore history push failures
    }
  };

  const normalizeCurrentHistoryToPlayerHome = () => {
    if (typeof window === 'undefined') return;
    try {
      window.history.replaceState({
        playerHome: true,
        playerNonce: `home-${Date.now()}`,
      }, '', playerHomePath);
    } catch {
      // ignore history replace failures
    }
  };

  const installPlayerHistoryTrap = () => {
    if (typeof window === 'undefined') return;
    try {
      parkPlayerHistoryAtHome({ resetStack: true });
    } catch {
      // ignore history push failures
    }
  };

  const pushOverlayHistory = (name) => {
    if (typeof window === 'undefined' || suppressOverlayHistoryRef.current) return;
    if (overlayStackRef.current[overlayStackRef.current.length - 1] === name) return;
    try {
      pushPlayerHomeGuardBuffer();
      window.history.pushState({
        ...(window.history.state || {}),
        playerOverlay: name,
        overlayRestore: overlayRestoreRef.current[name] || null,
        overlayNonce: `${name}-${Date.now()}`,
      }, '');
      overlayStackRef.current.push(name);
    } catch {
      // ignore history push failures
    }
  };

  const dropOverlayFromStack = (name) => {
    const stack = overlayStackRef.current;
    const idx = stack.lastIndexOf(name);
    if (idx >= 0) stack.splice(idx, 1);
  };

  const replaceCurrentOverlayHistory = (name, restore = null) => {
    if (typeof window === 'undefined') return;
    try {
      window.history.replaceState({
        ...(window.history.state || {}),
        playerOverlay: name,
        overlayRestore: restore,
        overlayNonce: `${name}-${Date.now()}`,
      }, '');
      const stack = overlayStackRef.current;
      if (stack.length) stack[stack.length - 1] = name;
      else stack.push(name);
    } catch {
      // ignore history replace failures
    }
  };

  const closeOverlayState = (name) => {
    dropOverlayFromStack(name);
    if (name === 'reset-password') {
      setResetPwOpen(false);
      if (overlayRestoreRef.current['reset-password'] === 'profile') setProfileOpen(true);
      return;
    }
    if (name === 'wallet') {
      setWalletOpen(false);
      setCashoutStage('choice');
      return;
    }
    if (name === 'balance-detail') {
      setBalanceDetailOpen(false);
      return;
    }
    if (name === 'edit-profile') {
      setEditProfileOpen(false);
      setEditProfileMode(null);
      if (overlayRestoreRef.current['edit-profile'] === 'profile') setProfileOpen(true);
      return;
    }
    if (name === 'phone-verification') {
      setPhoneVerificationBackSignal(value => value + 1);
      return;
    }
    if (name === 'add-game') {
      setAddGameOpen(false);
      return;
    }
    if (name === 'profile') {
      setProfileOpen(false);
      return;
    }
    if (name === 'chat') {
      setChatOpen(false);
      setTimeout(() => fetchUnread(), 80);
      if (overlayRestoreRef.current.chat === 'support') setSupportMenuOpen(true);
      return;
    }
    if (name === 'alerts') {
      setNotificationsOpen(false);
      setNotificationDetailOpen(false);
      setNotificationDetailItem(null);
      if (overlayRestoreRef.current.alerts === 'support') setSupportMenuOpen(true);
      return;
    }
    if (name === 'notification-detail') {
      setNotificationDetailOpen(false);
      setNotificationDetailItem(null);
      return;
    }
    if (name === 'home-alerts') {
      setHomeAlertsOpen(false);
      return;
    }
    if (name === 'support') {
      setSupportMenuOpen(false);
      return;
    }
    if (name === 'announcements') {
      setAnnouncementsOpen(false);
      return;
    }
    if (name === 'achievements') {
      setAchievementsOpen(false);
    }
  };

  const closeOverlayWithBack = (name, fallbackClose) => {
    dropOverlayFromStack(name);
    fallbackClose?.();
    if (typeof window !== 'undefined' && window.history.state?.playerOverlay === name) {
      normalizeCurrentHistoryToPlayerHome();
    }
  };

  const openSupportMenu = () => {
    delete overlayRestoreRef.current.support;
    pushOverlayHistory('support');
    setSupportMenuOpen(true);
  };

  const closeSupportMenu = () => closeOverlayWithBack('support', () => setSupportMenuOpen(false));

  const openChat = ({ restoreTo = null } = {}) => {
    if (restoreTo) overlayRestoreRef.current.chat = restoreTo;
    else delete overlayRestoreRef.current.chat;
    pushOverlayHistory('chat');
    setChatOpen(true);
  };

  const moveSupportToChat = (agent = null) => {
    withOverlayHistorySuppressed(() => {
      setSelectedChatAgent(agent || null);
      setOpenCommunityFromSupport(false);
      delete overlayRestoreRef.current.chat;
      replaceCurrentOverlayHistory('chat', null);
      setSupportMenuOpen(false);
      setChatOpen(true);
    });
  };

  const moveSupportToCommunity = () => {
    withOverlayHistorySuppressed(() => {
      setSelectedChatAgent(null);
      setOpenCommunityFromSupport(true);
      delete overlayRestoreRef.current.chat;
      replaceCurrentOverlayHistory('chat', null);
      setSupportMenuOpen(false);
      setChatOpen(true);
    });
  };

  const moveChatToSupport = () => {
    withOverlayHistorySuppressed(() => {
      setChatOpen(false);
      setChatUnread(0);
      setSelectedChatAgent(null);
      setOpenCommunityFromSupport(false);
      delete overlayRestoreRef.current.chat;
      replaceCurrentOverlayHistory('support', null);
      setSupportMenuOpen(true);
      setTimeout(() => fetchUnread(), 80);
    });
  };

  const closeChat = () => closeOverlayWithBack('chat', () => {
    setChatOpen(false);
    setChatUnread(0);
    setOpenCommunityFromSupport(false);
  });

  const openAlertsPanel = ({ restoreTo = null } = {}) => {
    if (restoreTo) overlayRestoreRef.current.alerts = restoreTo;
    else delete overlayRestoreRef.current.alerts;
    pushOverlayHistory('alerts');
    setNotificationsOpen(true);
  };

  const closeAlertsPanel = () => {
    const activeOverlay = typeof window !== 'undefined' ? window.history.state?.playerOverlay : null;
    closeOverlayWithBack('alerts', () => {
      setNotificationsOpen(false);
      setNotificationDetailOpen(false);
      setNotificationDetailItem(null);
      dropOverlayFromStack('notification-detail');
    });
    if (activeOverlay === 'notification-detail') {
      normalizeCurrentHistoryToPlayerHome();
    }
  };

  const openNotificationDetail = (item) => {
    if (!item) return;
    setNotificationDetailItem(item);
    pushOverlayHistory('notification-detail');
    setNotificationDetailOpen(true);
  };

  const closeNotificationDetail = () => closeOverlayWithBack('notification-detail', () => {
    setNotificationDetailOpen(false);
    setNotificationDetailItem(null);
  });

  const openHomeAlertsPreview = () => {
    pushOverlayHistory('home-alerts');
    setHomeAlertsOpen(true);
    void loadHomeAlerts();
  };

  const closeHomeAlertsPreview = () => closeOverlayWithBack('home-alerts', () => setHomeAlertsOpen(false));

  const openAlertsFromHomePreview = () => {
    setHomeAlertsOpen(false);
    dropOverlayFromStack('home-alerts');
    if (typeof window !== 'undefined' && window.history.state?.playerOverlay === 'home-alerts') {
      delete overlayRestoreRef.current.alerts;
      replaceCurrentOverlayHistory('alerts', null);
      setNotificationsOpen(true);
      return;
    }
    openAlertsPanel();
  };

  const openProfile = ({ restoreTo = null } = {}) => {
    if (restoreTo) overlayRestoreRef.current.profile = restoreTo;
    else delete overlayRestoreRef.current.profile;
    pushOverlayHistory('profile');
    setProfileOpen(true);
  };

  const closeProfile = () => closeOverlayWithBack('profile', () => setProfileOpen(false));

  const openEditProfile = ({ restoreTo = null, mode = null } = {}) => {
    if (restoreTo) overlayRestoreRef.current['edit-profile'] = restoreTo;
    else delete overlayRestoreRef.current['edit-profile'];
    setEditProfileMode(mode);
    pushOverlayHistory('edit-profile');
    setEditProfileOpen(true);
  };

  const closeEditProfile = () => closeOverlayWithBack('edit-profile', () => {
    setEditProfileOpen(false);
    setEditProfileMode(null);
  });

  const openResetPassword = ({ restoreTo = null } = {}) => {
    if (restoreTo) overlayRestoreRef.current['reset-password'] = restoreTo;
    else delete overlayRestoreRef.current['reset-password'];
    pushOverlayHistory('reset-password');
    setResetPwOpen(true);
  };

  const closeResetPassword = () => closeOverlayWithBack('reset-password', () => setResetPwOpen(false));

  const openAddGame = () => {
    delete overlayRestoreRef.current['add-game'];
    pushOverlayHistory('add-game');
    setAddGameOpen(true);
  };

  const closeAddGame = () => closeOverlayWithBack('add-game', () => setAddGameOpen(false));

  const openBalanceDetail = () => {
    dismissBalanceCoach();
    delete overlayRestoreRef.current['balance-detail'];
    pushOverlayHistory('balance-detail');
    setBalanceDetailOpen(true);
  };

  const closeBalanceDetail = () => closeOverlayWithBack('balance-detail', () => setBalanceDetailOpen(false));

  const openIdentityVerificationFromWallet = (event = null) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    event?.nativeEvent?.stopImmediatePropagation?.();
    tap();
    closeBalanceDetail();
    setProfileIdentityFocusKey((value) => value + 1);
    openProfile();
  };

  const keepWalletTransferLayerOpen = (event) => {
    const target = event?.detail?.originalEvent?.target || event?.target;
    if (target?.closest?.('[data-wallet-transfer-layer="true"]')) {
      event.preventDefault();
    }
  };

  const openAnnouncements = () => {
    pushOverlayHistory('announcements');
    setAnnouncementsOpen(true);
  };

  const closeAnnouncements = () => closeOverlayWithBack('announcements', () => setAnnouncementsOpen(false));

  const openAchievements = () => {
    navigate(`${playerNavigationBase(tenantSlug)}/achievements`);
  };

  const closeAchievements = () => closeOverlayWithBack('achievements', () => setAchievementsOpen(false));

  const dismissBalanceCoach = () => {
    setBalanceCoachVisible(false);
    if (!balanceCoachStorageKey) return;
    try {
      localStorage.setItem(balanceCoachStorageKey, 'done');
    } catch {
      // Best-effort onboarding hint; storage failures should never block play.
    }
  };

  const toggleFavorite = (gameId) => {
    setFavorites(prev => {
      const next = new Set(prev);
      if (next.has(gameId)) next.delete(gameId); else next.add(gameId);
      localStorage.setItem(favoritesStorageKey, JSON.stringify([...next]));
      return next;
    });
  };

  useEffect(() => {
    installPlayerHistoryTrap();
    // Fetch verified-badge payload (tier color + phone_verified gate)
    api.get('/api/me/badge').then(r => setBadge(b => ({ ...b, ...(r.data || {}) }))).catch(() => {});
    const t = setInterval(fetchUnread, 10000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    activeBrandingScopeRef.current = brandingScope;
    fetchBranding({ scope: brandingScope });

    const intervalId = setInterval(() => {
      fetchBranding({ scope: brandingScope });
    }, BRANDING_REFRESH_INTERVAL_MS);

    const refreshOnFocus = () => fetchBranding({ scope: brandingScope });
    const refreshOnVisible = () => {
      if (document.visibilityState === 'visible') refreshOnFocus();
    };

    window.addEventListener('focus', refreshOnFocus);
    document.addEventListener('visibilitychange', refreshOnVisible);
    return () => {
      clearInterval(intervalId);
      window.removeEventListener('focus', refreshOnFocus);
      document.removeEventListener('visibilitychange', refreshOnVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandingScope]);

  useEffect(() => {
    if (!selectedChatAgent) return;
    const agents = (Array.isArray(effectiveBranding.chat_agents) ? effectiveBranding.chat_agents : [])
      .filter(agent => agent && agent.enabled !== false && String(agent.name || '').trim());
    const selectedKey = chatAgentKey(selectedChatAgent);
    const latest = agents.find(agent => {
      if (!selectedKey) return false;
      if (chatAgentKey(agent) === selectedKey) return true;
      return String(agent.id || '') && String(agent.id) === String(selectedChatAgent.id || '');
    });

    if (!latest) {
      setSelectedChatAgent(null);
      return;
    }

    const normalizedLatest = {
      id: latest.id || selectedChatAgent.id || '',
      name: String(latest.name || '').trim(),
      title: latest.title || 'Support Agent',
      avatar_url: latest.avatar_url || '',
    };
    if (
      String(normalizedLatest.id || '') !== String(selectedChatAgent.id || '') ||
      normalizedLatest.name !== String(selectedChatAgent.name || '') ||
      normalizedLatest.title !== String(selectedChatAgent.title || '') ||
      normalizedLatest.avatar_url !== String(selectedChatAgent.avatar_url || '')
    ) {
      setSelectedChatAgent(normalizedLatest);
    }
  }, [effectiveBranding.chat_agents, selectedChatAgent]);

  useEffect(() => {
    if (loading || !user?.id || !balanceCoachStorageKey) return undefined;
    try {
      if (localStorage.getItem(balanceCoachStorageKey) === 'done') return undefined;
    } catch {
      // Continue with the hint if storage is unavailable.
    }

    const startTimer = setTimeout(() => setBalanceCoachVisible(true), 550);
    return () => {
      clearTimeout(startTimer);
    };
  }, [loading, user?.id, balanceCoachStorageKey]);

  useEffect(() => {
    // Auto-open profile when ?open=profile is present (e.g., from Explore "Verify phone" CTA)
    try {
      const params = new URLSearchParams(location.search || window.location.search);
      const openParam = params.get('open');
      if (openParam === 'profile') {
        openProfile();
      } else if (openParam === 'phone-verify') {
        // Land the user directly inside EditProfilePanel where phone
        // verification lives, but keep ProfileFullScreen behind it so
        // closing the editor returns the player to the profile screen.
        openProfile();
        openEditProfile({ restoreTo: 'profile', mode: 'phone-verify' });
      } else if (openParam === 'support' || openParam === 'chat') {
        openSupportMenu();
      }
      if (openParam) {
        // Clean the query string so refreshes don't re-trigger it.
        const cleaned = window.location.pathname + window.location.hash;
        window.history.replaceState({ playerHome: true, playerNonce: `home-${Date.now()}` }, '', cleaned);
      }
    } catch { /* ignore */ }
  }, [location.search]);

  useEffect(() => {
    if (!user?.id || user?.role !== 'player') return undefined;

    let lastPresenceAt = 0;
    const sendPresence = () => {
      if (document.visibilityState === 'hidden') return;
      const now = Date.now();
      if (now - lastPresenceAt < 15000) return;
      lastPresenceAt = now;
      liveChatApi.post('/api/chat/presence/heartbeat').catch(() => {});
    };
    const markActivity = () => sendPresence();

    sendPresence();
    const interval = setInterval(sendPresence, 45000);
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') sendPresence();
    };
    const activityEvents = ['pointerdown', 'keydown', 'touchstart', 'scroll', 'mousemove'];

    window.addEventListener('focus', sendPresence);
    activityEvents.forEach(eventName => window.addEventListener(eventName, markActivity, { passive: true }));
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', sendPresence);
      activityEvents.forEach(eventName => window.removeEventListener(eventName, markActivity));
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [user?.id, user?.role]);

  const accountMatchesPending = (pending, account) => {
    if (!pending || !account) return false;
    if (pending.id && account.id && String(pending.id) === String(account.id)) return true;
    if (pending.game_id && account.game_id && String(pending.game_id) === String(account.game_id)) return true;
    if (pending.game_code && account.game_code && String(pending.game_code) === String(account.game_code)) return true;
    return false;
  };

  const accountHasReadyCredentials = (account) => Boolean(
    account?.username ||
    account?.provider_username ||
    account?.password ||
    account?.provider_password ||
    account?.provider_account_id ||
    account?.provider_user_id
  );

  const accountReplacesPending = (pending, account) => (
    accountMatchesPending(pending, account) && accountHasReadyCredentials(account)
  );

  const toLocalOptimisticGameAccount = (account) => {
    if (!account?.id) return null;
    const now = new Date().toISOString();
    return {
      ...account,
      __creating: false,
      __localOptimistic: true,
      __localPinnedAt: account.__localPinnedAt || now,
      last_activity_at: account.last_activity_at || account.created_at || now,
    };
  };

  const dedupeLocalGameAccounts = (accounts) => {
    const next = [];
    accounts.forEach(account => {
      if (!account?.id) return;
      if (next.some(existing => (
        existing.id === account.id ||
        accountMatchesPending(existing, account)
      ))) return;
      next.push(account);
    });
    return next;
  };

  const mergeReadyServerAccounts = (serverAccounts) => {
    const readyAccounts = (serverAccounts || []).filter(accountHasReadyCredentials);

    setPendingGameAccounts(prev => {
      let changed = false;
      const next = prev.flatMap(pending => {
        const replacement = readyAccounts.find(serverAcc => accountReplacesPending(pending, serverAcc));
        if (!replacement) {
          if (pending.__localOptimistic) {
            changed = true;
            return [];
          }
          return [pending];
        }
        if (pending.__localOptimistic) {
          changed = true;
          return [];
        }

        const cachedReplacement = toLocalOptimisticGameAccount(replacement);
        changed = true;
        return cachedReplacement ? [cachedReplacement] : [];
      });
      const deduped = dedupeLocalGameAccounts(next);
      return changed || deduped.length !== prev.length ? deduped : prev;
    });
  };

  const removePendingGameAccounts = (matcher) => {
    setPendingGameAccounts(prev => {
      if (!prev.length) return prev;
      if (typeof matcher === 'string') {
        const next = prev.filter(acc => acc.id !== matcher);
        return next.length === prev.length ? prev : next;
      }
      const next = prev.filter(acc => !accountReplacesPending(acc, matcher));
      return next.length === prev.length ? prev : next;
    });
  };

  const pinLocalGameAccount = (account, options = {}) => {
    const localAccount = toLocalOptimisticGameAccount(account);
    if (!localAccount) return null;
    const replaceId = options.replaceId;
    setPendingGameAccounts(prev => dedupeLocalGameAccounts([
      localAccount,
      ...prev.filter(acc => acc.id !== replaceId),
    ]));
    setData(prev => {
      const previousAccounts = Array.isArray(prev.accounts) ? prev.accounts : [];
      return {
        ...prev,
        accounts: [
          localAccount,
          ...previousAccounts.filter(acc => (
            acc.id !== replaceId &&
            acc.id !== localAccount.id &&
            !accountMatchesPending(localAccount, acc)
          )),
        ],
      };
    });
    return localAccount;
  };

  useEffect(() => {
    if (!playerSessionScope) return;
    try {
      sessionStorage.setItem(pendingGamesStorageKey, JSON.stringify(pendingGameAccounts));
    } catch {
      // Ignore storage failures; the in-memory creating card still works.
    }
  }, [pendingGameAccounts, pendingGamesStorageKey, playerSessionScope]);

  const upsertLinkedGameAccountFromNotification = (notif) => {
    const metadata = notif?.metadata || {};
    const linked = metadata.account || metadata.game_account || {};
    const accountId = linked.id || metadata.account_id;
    if (!accountId) return false;

    const nextBalance = Number(
      linked.last_known_balance ??
      linked.balance_usd ??
      metadata.verified_balance ??
      0
    ) || 0;
    const nextAccount = {
      id: accountId,
      user_id: linked.user_id || user?.id,
      tenant_id: linked.tenant_id || user?.tenant_id,
      game_id: linked.game_id || metadata.game_id || linked.game_code || metadata.game_code,
      game_code: linked.game_code || metadata.game_code || linked.game_id || metadata.game_id,
      game_name: linked.game_name || metadata.game_name || 'Game',
      username: linked.username || linked.provider_username || metadata.provider_username || '',
      password: linked.password || linked.provider_password || '',
      provider_code: linked.provider_code || metadata.provider_code || linked.game_code || metadata.game_code,
      provider_game_id: linked.provider_game_id || metadata.provider_game_id || linked.provider_code || metadata.provider_code,
      provider_account_id: linked.provider_account_id || metadata.provider_account_id || '',
      provider_user_id: linked.provider_user_id || metadata.provider_user_id || '',
      last_known_balance: nextBalance,
      balance_usd: nextBalance,
      last_activity_at: linked.last_activity_at || linked.created_at || new Date().toISOString(),
      last_balance_synced_at: linked.last_balance_synced_at || new Date().toISOString(),
      created_via: linked.created_via || 'admin_backend_link',
    };

    pinLocalGameAccount(nextAccount);
    return true;
  };

  // Real-time notification stream. Fires the moment the backend creates a
  // notification for this player (e.g. someone signs up with their referral
  // code, a deposit is approved, etc).
  useNotificationsWS({
    token: typeof window !== 'undefined' ? localStorage.getItem('session_token') : null,
    enabled: !!user?.id,
    onNotification: (notif) => {
      const title = String(notif?.title || 'New notification');
      const message = String(notif?.message || '');
      const category = String(notif?.category || '').toLowerCase();
      const actionUrl = String(notif?.action_url || '');
      const metadataEvent = String(notif?.metadata?.event || '').toLowerCase();
      const normalized = `${title} ${message}`.toLowerCase();
      const isLegacyPlayCreditLowBalance =
        normalized.includes('low balance warning') &&
        normalized.includes('play credits balance is low');

      if (isLegacyPlayCreditLowBalance) {
        return;
      }

      const isGameAffecting =
        category === 'game' ||
        actionUrl.startsWith('/games') ||
        metadataEvent === 'provider_account_linked' ||
        metadataEvent === 'game_account_created' ||
        normalized.includes('game account linked');

      const isWalletAffecting =
        category === 'deposit' ||
        category === 'wallet' ||
        actionUrl === '/wallet' ||
        isGameAffecting ||
        normalized.includes('approved and credited') ||
        normalized.includes('deposit approved') ||
        normalized.includes('wallet updated') ||
        normalized.includes('credited automatically') ||
        normalized.includes('redeemed from');

      if (
        metadataEvent === 'provider_account_linked' ||
        metadataEvent === 'game_account_created' ||
        normalized.includes('game account linked') ||
        normalized.includes('your game is ready')
      ) {
        upsertLinkedGameAccountFromNotification(notif);
      }

      if (isWalletAffecting) {
        void syncPlayerBalances({ force: true });
      }

      // Lift unread badge instantly — no need to wait for the 10s poll.
      setNotifUnread((n) => n + 1);
      setHomeAlertItems(prev => [notif, ...prev.filter(item => item.id !== notif?.id)].slice(0, 20));
      if (normalized.includes('low balance warning')) {
        return;
      }
      // Flash a toast so the user sees the event even if the bell panel isn't open.
      const isDepositApproved =
        category === 'deposit' &&
        (normalized.includes('approved') || normalized.includes('credited'));
      const isGameLoadOrRedeem =
        normalized.includes('game load success') ||
        normalized.includes('game redeem success') ||
        normalized.includes('loaded into') ||
        normalized.includes('redeemed from');
      const toastTitle = isDepositApproved
        ? 'Your credit has been added'
        : (isGameLoadOrRedeem ? gameNotificationToastTitle(notif, title) : title);
      const toastDescription = isDepositApproved
        ? `${message} Your wallet is updated now.`
        : message;
      toast.success(toastTitle, {
        id: isGameLoadOrRedeem ? gameNotificationToastId(notif) : 'player-live-alert',
        description: toastDescription,
        duration: 2000,
        action: isGameLoadOrRedeem ? undefined : {
          label: 'Open Alerts',
          onClick: () => {
            toast.dismiss();
            openAlerts();
          },
        },
      });
    },
    onCircleUpdate: (msg) => {
      // A downline (indirect) referrer's circle just got a new signup.
      // Show a transient toast — do NOT bump inbox count because the upline
      // doesn't get a persisted notification for indirect activity.
      if (msg?.event === 'referral_signup') {
        toast(`Network growth · depth ${msg.depth}`, {
          description: `${msg.new_username} joined your referral circle`,
          duration: 4000,
        });
      }
    },
  });

  const fetchUnread = async () => {
    try {
      const res = await liveChatApi.get('/api/chat/unread-count');
      const nextChatUnread = res.data.unread_count || 0;
      const nextAgentUnread = Array.isArray(res.data.by_agent) ? res.data.by_agent : [];
      const previousChatUnread = previousChatUnreadRef.current;
      setChatUnread(nextChatUnread);
      setChatAgentUnread(nextAgentUnread);
      if (
        previousChatUnread !== null &&
        nextChatUnread > previousChatUnread &&
        !chatOpen
      ) {
        const newestAgent = nextAgentUnread[0];
        toast('New live chat message', {
          id: 'live-chat-unread',
          description: newestAgent?.agent_name
            ? `${newestAgent.agent_name}: ${newestAgent.last_message || 'sent you a message'}`
            : `${nextChatUnread > 99 ? '99+' : nextChatUnread} unread message${nextChatUnread === 1 ? '' : 's'}`,
          duration: 2000,
          action: {
            label: 'Pick Agent',
            onClick: openLiveChatFromNotice,
          },
          onClick: openLiveChatFromNotice,
        });
      }
      previousChatUnreadRef.current = nextChatUnread;
    } catch {}
    try {
      const r2 = await api.get('/api/notifications/unread-count');
      setNotifUnread(r2.data.count || 0);
    } catch {}
  };

  useEffect(() => {
    setBrowserAppBadge(notifUnread);
  }, [notifUnread]);

  const loadHomeAlerts = async () => {
    setHomeAlertsLoading(true);
    try {
      const res = await api.get('/api/notifications');
      const nextItems = res.data?.notifications || [];
      setHomeAlertItems(isLocalDemoVipAlertPreview() && nextItems.length === 0 ? [makeDemoVipRewardAlert()] : nextItems);
    } catch {
      setHomeAlertItems(isLocalDemoVipAlertPreview() ? [makeDemoVipRewardAlert()] : []);
    } finally {
      setHomeAlertsLoading(false);
    }
  };

  const handleHomeAlertsOpenChange = (open) => {
    if (open) {
      openHomeAlertsPreview();
    } else {
      closeHomeAlertsPreview();
    }
  };

  const markHomeAlertRead = async (item) => {
    if (!item) return;
    const actionUrl = item.action_url || item.actionUrl || '';
    const metadataEvent = String(item.metadata?.event || item.meta?.event || '').toLowerCase();
    const isRewardAlert = actionUrl && ['vip_reward_available', 'task_reward_available'].includes(metadataEvent);
    try {
      if (isNotificationUnread(item)) {
        await api.post(`/api/notifications/${item.id}/read`);
        setNotifUnread((count) => Math.max(0, count - 1));
        setHomeAlertItems(prev => prev.map(n => (
          n.id === item.id ? { ...n, read: true, is_read: true } : n
        )));
      }
    } catch {
      if (!isLocalDemoVipAlertPreview()) {
        toast.error('Failed');
        return;
      }
    }
    toast.dismiss();
    if (isRewardAlert) {
      setHomeAlertsOpen(false);
      dropOverlayFromStack('home-alerts');
      if (/^https?:\/\//i.test(actionUrl)) {
        window.location.href = actionUrl;
        return;
      }
      navigate(tenantSlug && actionUrl.startsWith('/app') ? `/${tenantSlug}${actionUrl}` : actionUrl);
      return;
    }
    openAlertsFromHomePreview();
  };

  const markHomeAlertsReadAll = async () => {
    try {
      await api.post('/api/notifications/mark-all-read');
      setNotifUnread(0);
      setHomeAlertItems(prev => prev.map(n => ({ ...n, read: true, is_read: true })));
      toast.dismiss();
    } catch {
      toast.error('Failed');
    }
  };

  const openAllAlertsFromHome = () => {
    openAlertsFromHomePreview();
  };

  const resetPlayerRuntimeState = () => {
    clearAllGameCreationTimers();
    setData(emptyPlayerData());
    setPlayerDataReadyScope('');
    setWallet({ balance: 0, pending: 0, play_credits: 0 });
    setWalletRules({ withdrawal_rules: { min_amount: 20, max_amount: 5000, max_total_per_day: 0 } });
    setBadge({ phone_verified: false, show_badge: false, tier: { key: 'none', color: '#3B82F6', label: 'Member' } });
    setChatUnread(0);
    setChatAgentUnread([]);
    setNotifUnread(0);
    setHomeAlertItems([]);
    setSearchQuery('');
    setFocusGameAccountId(null);
    setGameCreations({});
    setPendingGameAccounts([]);
    setWalletOpen(false);
    setCashoutStage('choice');
    setSupportMenuOpen(false);
    setChatOpen(false);
    setAddGameOpen(false);
    setProfileOpen(false);
    setBalanceDetailOpen(false);
    setNotificationsOpen(false);
    setHomeAlertsOpen(false);
    setAnnouncementsOpen(false);
    setAchievementsOpen(false);
    overlayStackRef.current = [];
    overlayRestoreRef.current = {};
    lastBalanceSyncAtRef.current = 0;
    walletSyncInFlightRef.current = false;
    balanceSyncInFlightRef.current = false;
  };

  const resolveUnreadChatAgent = () => {
    const unread = Array.isArray(chatAgentUnread)
      ? chatAgentUnread.find(item => Number(item?.unread_count || 0) > 0) || chatAgentUnread[0]
      : null;
    if (!unread) return null;

    const unreadId = String(unread.agent_id || '').trim();
    const unreadName = String(unread.agent_name || '').trim();
    const configuredAgents = (Array.isArray(effectiveBranding.chat_agents) ? effectiveBranding.chat_agents : [])
      .filter(agent => agent && agent.enabled !== false && String(agent.name || '').trim());

    const configured = configuredAgents.find(agent => {
      const agentId = String(agent.id || '').trim();
      const agentName = String(agent.name || '').trim().toLowerCase();
      return (unreadId && agentId === unreadId) || (unreadName && agentName === unreadName.toLowerCase());
    });

    return {
      id: configured?.id || unreadId || '',
      name: configured?.name || unreadName || 'Support Team',
      title: configured?.title || unread.agent_title || 'Support Agent',
      avatar_url: configured?.avatar_url || unread.avatar_url || '',
    };
  };

  const fetchData = async ({ scope = playerSessionScope, replaceAccounts = false, minimumLoadingMs = 0, fullLoading = false } = {}) => {
    const requestScope = scope || playerSessionScope;
    if (!requestScope) {
      setLoading(false);
      return;
    }
    const startedAt = Date.now();
    const revision = playerSessionRevision();
    if (fullLoading) setLoading(true);
    try {
      const [gamesRes, methodsRes, txnsRes, accsRes, walletRes, walletRulesRes] = await Promise.all([
        freshGet('/api/games/list', requestScope)
          .catch(() => freshGet('/api/public/games', requestScope))
          .catch(() => ({ data: { games: [] } })),
        api.get('/api/payment-methods', {
          timeout: 20000,
          params: { _ts: Date.now() },
          headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
        }).catch(() => ({ data: { items: [] } })),
        api.get('/api/requests/my-requests?limit=20', { timeout: 20000 }).catch(() => ({ data: { requests: [] } })),
        freshGet('/api/games/accounts', requestScope).catch(() => ({ data: { accounts: [], __failed: true } })),
        api.get('/api/wallet/balance', { timeout: 20000 }).catch(() => null),
        api.get('/api/public/settings/wallet-rules', { timeout: 20000 }).catch(() => null),
      ]);
      if (activePlayerScopeRef.current !== requestScope || revision !== playerSessionRevision()) return;
      if (walletRes?.data) setWallet(walletRes.data);
      if (walletRulesRes?.data) setWalletRules(walletRulesRes.data);
      const nextGames = Array.isArray(gamesRes.data)
        ? gamesRes.data
        : (gamesRes.data.games || gamesRes.data.items || []);
      const nextMethods = sortPlayerPaymentMethods(methodsRes.data.items || methodsRes.data.methods || []);
      const accountsFetchFailed = Boolean(accsRes.data.__failed);
      const serverAccounts = accountsFetchFailed
        ? []
        : (Array.isArray(accsRes.data.accounts) ? accsRes.data.accounts : []);
      if (!accountsFetchFailed) mergeReadyServerAccounts(serverAccounts);
      setData(prev => ({
        games: nextGames.length ? nextGames : prev.games,
        methods: nextMethods,
        transactions: txnsRes.data.requests || txnsRes.data.transactions || [],
        accounts: accountsFetchFailed
          ? prev.accounts
          : replaceAccounts
          ? serverAccounts
          : mergeServerAccountsPreservingLive(prev.accounts, serverAccounts),
      }));
    } finally {
      if (activePlayerScopeRef.current === requestScope && revision === playerSessionRevision()) {
        const waitMs = Math.max(0, Number(minimumLoadingMs || 0) - (Date.now() - startedAt));
        if (waitMs > 0) {
          await new Promise(resolve => setTimeout(resolve, waitMs));
        }
        if (activePlayerScopeRef.current !== requestScope || revision !== playerSessionRevision()) return;
        setPlayerDataReadyScope(requestScope);
        readyLobbyKeyRef.current = lobbyKey;
        setLoading(false);
        void syncPlayerBalances({ force: true, scope: requestScope });
      }
    }
  };

  const refreshPaymentMethods = async (flow = 'deposit') => {
    try {
      const methodsRes = await api.get('/api/payment-methods', {
        params: { flow, _ts: Date.now() },
        headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
      });
      const nextMethods = sortPlayerPaymentMethods(methodsRes.data.items || methodsRes.data.methods || []);
      setData(prev => ({
        ...prev,
        methods: nextMethods,
      }));
    } catch {
      // Keep the wallet usable with the last known methods if the refresh fails.
    }
  };

  useEffect(() => {
    if (!user?.id) {
      activePlayerScopeRef.current = '';
      resetPlayerRuntimeState();
      setFavorites(new Set());
      setLoading(false);
      return;
    }

    activePlayerScopeRef.current = playerSessionScope;
    lobbyRevisionRef.current = selectPlayerSession(lobbyKey);
    const cached = readPlayerLobby(lobbyKey);
    readyLobbyKeyRef.current = cached ? lobbyKey : '';
    setLoading(!cached);
    setPlayerDataReadyScope(cached ? playerSessionScope : '');
    setAnnouncementCycle(!cached);
    try {
      sessionStorage.removeItem(PENDING_GAMES_STORAGE_KEY);
      Object.keys(sessionStorage).forEach(key => {
        if (key.startsWith('vl_player_accounts_cache')) sessionStorage.removeItem(key);
      });
    } catch {
      // ignore legacy storage cleanup failures
    }

    setPendingGameAccounts(readStoredArray(sessionStorage, pendingGamesStorageKey).filter(account => !account.__creating));
    setFavorites(new Set(readStoredArray(localStorage, favoritesStorageKey)));
    setData(cached?.data || emptyPlayerData());
    setWallet(cached?.wallet || { balance: 0, pending: 0, play_credits: 0 });
    setWalletRules(cached?.walletRules || { withdrawal_rules: { min_amount: 20, max_amount: 5000, max_total_per_day: 0 } });
    setGameCreations({});
    setSearchQuery('');
    setFocusGameAccountId(null);
    setHomeAlertItems([]);
    lastBalanceSyncAtRef.current = 0;
    walletSyncInFlightRef.current = false;
    balanceSyncInFlightRef.current = false;

    fetchData({ scope: playerSessionScope, replaceAccounts: true, fullLoading: !cached, minimumLoadingMs: cached ? 0 : PLAYER_DATA_MIN_LOADING_MS });
    fetchUnread();
    api.get('/api/me/badge').then(r => {
      if (activePlayerScopeRef.current === playerSessionScope) {
        setBadge(b => ({ ...b, ...(r.data || {}) }));
      }
    }).catch(() => {});
    const onIdle = () => {
      lobbyRevisionRef.current = playerSessionRevision();
      setPlayerDataReadyScope('');
      setAnnouncementCycle(true);
      fetchData({ scope: playerSessionScope, replaceAccounts: true, fullLoading: true });
    };
    window.addEventListener(PLAYER_IDLE_EVENT, onIdle);
    return () => {
      activePlayerScopeRef.current = '';
      window.removeEventListener(PLAYER_IDLE_EVENT, onIdle);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerSessionScope, lobbyKey, user?.id, favoritesStorageKey, pendingGamesStorageKey]);

  useEffect(() => {
    if (!loading && playerDataReadyScope === playerSessionScope && readyLobbyKeyRef.current === lobbyKey) {
      savePlayerLobby(lobbyKey, lobbyRevisionRef.current, { data, wallet, walletRules, branding });
    }
  }, [loading, playerDataReadyScope, playerSessionScope, lobbyKey, data, wallet, walletRules, branding]);

  const patchPlayerAccount = (accountId, patch = {}) => {
    if (!accountId) return;
    const nextPatch = {
      ...patch,
      updated_at: patch.updated_at || new Date().toISOString(),
    };
    setData(prev => ({
      ...prev,
      accounts: (Array.isArray(prev.accounts) ? prev.accounts : []).map(account => (
        account.id === accountId ? { ...account, ...nextPatch } : account
      )),
    }));
    setPendingGameAccounts(prev => prev.map(account => (
      account.id === accountId ? { ...account, ...nextPatch } : account
    )));
  };

  const syncPlayerBalances = async ({ force = false, liveBalances = false, scope = playerSessionScope } = {}) => {
    const requestScope = scope || playerSessionScope;
    const revision = playerSessionRevision();
    if (!requestScope || activePlayerScopeRef.current !== requestScope) return;
    if (!liveBalances) {
      if (walletSyncInFlightRef.current) return;
      if (!force && Date.now() - lastBalanceSyncAtRef.current < 12000) return;
      walletSyncInFlightRef.current = true;
      try {
        const [walletRes, accsRes] = await Promise.all([
          api.get('/api/wallet/balance').catch(() => null),
          freshGet('/api/games/accounts', requestScope).catch(() => null),
        ]);
        if (activePlayerScopeRef.current !== requestScope || revision !== playerSessionRevision()) return;
        if (walletRes?.data) setWallet(walletRes.data);
        if (accsRes?.data) {
          const accountRows = Array.isArray(accsRes.data.accounts) ? accsRes.data.accounts : [];
          mergeReadyServerAccounts(accountRows);
          setData(prev => ({
            ...prev,
            accounts: mergeServerAccountsPreservingLive(prev.accounts, accountRows),
          }));
        }
      } finally {
        lastBalanceSyncAtRef.current = Date.now();
        walletSyncInFlightRef.current = false;
      }
      return;
    }

    const now = Date.now();
    if (balanceSyncInFlightRef.current) return;
    if (!force && now - lastBalanceSyncAtRef.current < 12000) return;
    balanceSyncInFlightRef.current = true;
    try {
      const [accsRes, walletRes] = await Promise.all([
        api.post('/api/games/accounts/refresh-balances?live=true')
          .catch(() => freshGet('/api/games/accounts', requestScope).catch(() => null)),
        api.get('/api/wallet/balance').catch(() => null),
      ]);
      if (activePlayerScopeRef.current !== requestScope || revision !== playerSessionRevision()) return;

      if (walletRes?.data) {
        setWallet(walletRes.data);
      }

      if (accsRes?.data) {
        const accountRows = Array.isArray(accsRes.data.accounts) ? accsRes.data.accounts : [];
        if (accsRes.data.ok === true) {
          setData(prev => ({
            ...prev,
            accounts: mergeLiveBalanceRows(prev.accounts, accountRows),
          }));
        } else {
          mergeReadyServerAccounts(accountRows);
          setData(prev => ({
            ...prev,
            accounts: mergeServerAccountsPreservingLive(prev.accounts, accountRows),
          }));
        }
      }
    } catch {
      // Keep background sync quiet. Card-level actions already surface errors.
    } finally {
      lastBalanceSyncAtRef.current = Date.now();
      balanceSyncInFlightRef.current = false;
    }
  };

  useEffect(() => {
    if (!user?.id) return undefined;

    const tick = () => {
      if (document.visibilityState === 'visible') {
        void syncPlayerBalances();
      }
    };

    const interval = setInterval(tick, 15000);
    document.addEventListener('visibilitychange', tick);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [user?.id]);

  useEffect(() => {
    const onPopState = (event) => {
      event?.preventDefault?.();
      event?.stopPropagation?.();
      event?.stopImmediatePropagation?.();
      let handled = false;

      withOverlayHistorySuppressed(() => {
        if (typeof window !== 'undefined' && window.location.pathname !== playerHomePath) {
          try {
            window.history.replaceState({
              playerHome: true,
              playerGuard: true,
              playerNonce: `route-correct-${Date.now()}`,
            }, '', playerHomePath);
          } catch {
            // ignore route correction failures
          }
        }
        const stackedOverlay = overlayStackRef.current.pop();
        if (stackedOverlay) {
          closeOverlayState(stackedOverlay);
          schedulePlayerHomeGuardBuffer();
          handled = true;
          return;
        }
        if (notificationDetailOpen) {
          setNotificationDetailOpen(false);
          setNotificationDetailItem(null);
          schedulePlayerHomeGuardBuffer();
          handled = true;
          return;
        }
        if (resetPwOpen) {
          setResetPwOpen(false);
          const restore = overlayRestoreRef.current['reset-password'];
          if (restore === 'profile') setProfileOpen(true);
          schedulePlayerHomeGuardBuffer();
          handled = true;
          return;
        }
        if (balanceDetailOpen) {
          setBalanceDetailOpen(false);
          schedulePlayerHomeGuardBuffer();
          handled = true;
          return;
        }
        if (editProfileOpen) {
          setEditProfileOpen(false);
          setEditProfileMode(null);
          const restore = overlayRestoreRef.current['edit-profile'];
          if (restore === 'profile') setProfileOpen(true);
          schedulePlayerHomeGuardBuffer();
          handled = true;
          return;
        }
        if (addGameOpen) {
          setAddGameOpen(false);
          schedulePlayerHomeGuardBuffer();
          handled = true;
          return;
        }
        if (profileOpen) {
          setProfileOpen(false);
          schedulePlayerHomeGuardBuffer();
          handled = true;
          return;
        }
        if (chatOpen) {
          setChatOpen(false);
          setTimeout(() => fetchUnread(), 80);
          const restore = overlayRestoreRef.current.chat;
          if (restore === 'support') setSupportMenuOpen(true);
          schedulePlayerHomeGuardBuffer();
          handled = true;
          return;
        }
        if (notificationsOpen) {
          setNotificationsOpen(false);
          const restore = overlayRestoreRef.current.alerts;
          if (restore === 'support') setSupportMenuOpen(true);
          schedulePlayerHomeGuardBuffer();
          handled = true;
          return;
        }
        if (homeAlertsOpen) {
          setHomeAlertsOpen(false);
          schedulePlayerHomeGuardBuffer();
          handled = true;
          return;
        }
        if (supportMenuOpen) {
          setSupportMenuOpen(false);
          schedulePlayerHomeGuardBuffer();
          handled = true;
          return;
        }
        if (window.history.state?.playerRoot) {
          handled = true;
          schedulePlayerHomeGuardBuffer();
          return;
        }
        if (window.history.state?.playerHome) {
          handled = true;
          schedulePlayerHomeGuardBuffer();
          return;
        }
      });

      if (!handled) {
        schedulePlayerHomeGuardBuffer();
        suppressOverlayHistoryRef.current = false;
      }
    };

    window.addEventListener('popstate', onPopState, true);
    return () => {
      window.removeEventListener('popstate', onPopState, true);
      if (overlayReleaseTimerRef.current) {
        clearTimeout(overlayReleaseTimerRef.current);
      }
      if (historyGuardPushTimerRef.current) {
        clearTimeout(historyGuardPushTimerRef.current);
      }
    };
  }, [playerHomePath, supportMenuOpen, chatOpen, notificationsOpen, notificationDetailOpen, homeAlertsOpen, profileOpen, editProfileOpen, resetPwOpen, addGameOpen, balanceDetailOpen, walletOpen, announcementsOpen, achievementsOpen]);

  const handleLogout = async () => {
    activePlayerScopeRef.current = '';
    resetPlayerRuntimeState();
    setFavorites(new Set());
    clearStoredPhoneVerificationAttempt(user);
    try {
      sessionStorage.removeItem(pendingGamesStorageKey);
      sessionStorage.removeItem(PENDING_GAMES_STORAGE_KEY);
      Object.keys(sessionStorage).forEach(key => {
        if (key.startsWith('vl_player_accounts_cache')) sessionStorage.removeItem(key);
      });
    } catch {
      // ignore logout storage cleanup failures
    }
    await logout();
    window.location.href = tenantSlug ? `/${tenantSlug}/auth` : '/auth';
  };

  const openPurchase = () => {
    pushOverlayHistory('wallet');
    setWalletMode('deposit');
    setCashoutStage('flow');
    setWalletMethod(null);
    setWalletStep(1);
    setWalletOpen(true);
    void refreshPaymentMethods('deposit');
  };
  const openCashout  = () => {
    pushOverlayHistory('wallet');
    setWalletMode('withdrawal');
    setCashoutStage('choice');
    setWalletMethod(null);
    setWalletStep(1);
    setWalletOpen(true);
  };
  const startCashout = (speed) => {
    setCashoutSpeed(['priority', 'express', 'normal'].includes(speed) ? speed : 'normal');
    setWalletMethod(null);
    setWalletStep(1);
    setCashoutStage('flow');
    void refreshPaymentMethods('withdraw');
  };
  const openAlerts = () => {
    toast.dismiss();
    const restoreTo = supportMenuOpen ? 'support' : null;
    if (supportMenuOpen) {
      withOverlayHistorySuppressed(() => {
        setSupportMenuOpen(false);
        openAlertsPanel({ restoreTo });
      });
      return;
    }
    openAlertsPanel({ restoreTo });
  };

  const openLiveChatFromNotice = () => {
    toast.dismiss('live-chat-unread');
    const unreadAgent = resolveUnreadChatAgent();
    if (notificationsOpen) {
      setNotificationsOpen(false);
    }
    if (unreadAgent) {
      moveSupportToChat(unreadAgent);
    } else if (chatOpen) {
      withOverlayHistorySuppressed(() => {
        setChatOpen(false);
        setTimeout(() => setSupportMenuOpen(true), 180);
      });
    } else {
      setSupportMenuOpen(true);
    }
  };

  // Iter 145+: a global "open the deposit flow" trigger that any deeply
  // nested component (e.g. NoGameBalanceModal inside GameAccountCard, or a
  // promo banner) can fire via `window.dispatchEvent(new CustomEvent('vl:open-deposit'))`
  // without prop-drilling. Listener stays mounted for the lifetime of PlayerApp.
  useEffect(() => {
    const onOpenDeposit = () => openPurchase();
    const onOpenReferral = () => navigate(tenantSlug ? `/${tenantSlug}/app/referral` : '/app/referral');
    const onOpenSupport = () => openSupportMenu();
    window.addEventListener('vl:open-deposit', onOpenDeposit);
    window.addEventListener('vl:open-referral', onOpenReferral);
    window.addEventListener('vl:open-support', onOpenSupport);
    return () => {
      window.removeEventListener('vl:open-deposit', onOpenDeposit);
      window.removeEventListener('vl:open-referral', onOpenReferral);
      window.removeEventListener('vl:open-support', onOpenSupport);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantSlug]);

  // Kicks off a real /api/games/create-account request and tracks its
  // lifecycle in `gameCreations` so the UI can reflect creating /
  // success / error even after the Add Games dialog has been closed.
  // Multiple in-flight creations can run in parallel.
  const startGameCreation = (game) => {
    if (!game) return;
    const pendingAccountId = `creating-${game.id}-${Date.now()}`;
    const creationKey = game.id || pendingAccountId;
    const supportMessage = `We couldn't create your ${game.name || 'game'} ID. Please contact support.`;
    const pendingAccount = {
      id: pendingAccountId,
      user_id: user?.id,
      tenant_id: user?.tenant_id,
      game_id: game.id,
      game_code: game.code || game.id,
      game_name: game.name || 'Your game',
      username: '',
      password: '',
      provider_code: game.provider_code || game.provider || game.code || 'creating',
      provider_game_id: game.provider_game_id || game.provider_id || game.provider_code || game.code || game.id,
      image_url: game.logo_image_url || game.logo_url || game.image_url || game.image || null,
      last_known_balance: 0,
      balance_usd: 0,
      __creating: true,
      created_at: new Date().toISOString(),
    };
    setSearchQuery('');
    setPendingGameAccounts(prev => [
      pendingAccount,
      ...prev.filter(acc => !accountMatchesPending(pendingAccount, acc)),
    ]);
    setFocusGameAccountId(pendingAccountId);
    setTimeout(() => {
      setFocusGameAccountId(current => (current === pendingAccountId ? null : current));
    }, 1800);
    setGameCreations(prev => ({
      ...prev,
      [creationKey]: { status: 'creating', game, pendingAccountId, error: null },
    }));
    clearGameCreationTimer(creationKey);
    gameCreationTimersRef.current[String(creationKey)] = setTimeout(() => {
      removePendingGameAccounts(pendingAccountId);
      setFocusGameAccountId(current => (current === pendingAccountId ? null : current));
      setGameCreations(prev => {
        const current = prev[creationKey];
        if (!current || current.pendingAccountId !== pendingAccountId || current.status !== 'creating') return prev;
        return {
          ...prev,
          [creationKey]: { status: 'error', game, pendingAccountId: null, error: supportMessage },
        };
      });
      toast.error(supportMessage, {
        id: `game-create-${creationKey}`,
        duration: 3500,
      });
      setTimeout(() => {
        setGameCreations(prev => {
          if (prev[creationKey]?.error !== supportMessage) return prev;
          const next = { ...prev };
          delete next[creationKey];
          return next;
        });
      }, 4000);
      delete gameCreationTimersRef.current[String(creationKey)];
    }, GAME_CREATE_CARD_TIMEOUT_MS);
    api.post('/api/games/create-account', { game_id: game.id })
      .then(res => {
        clearGameCreationTimer(creationKey);
        const username = res.data?.username || res.data?.provider_username || '';
        const readyAccount = {
          ...res.data,
          last_activity_at: res.data?.last_activity_at || res.data?.created_at || new Date().toISOString(),
        };
        const localReadyAccount = pinLocalGameAccount(readyAccount, {
          replaceId: pendingAccountId,
        }) || readyAccount;
        setGameCreations(prev => ({
          ...prev,
          [creationKey]: { status: 'success', game, account: localReadyAccount, error: null },
        }));
        toast.success('Your game is ready', {
          id: 'player-live-alert',
          description: `${game.name} added${username ? ` — ${username}` : ''}`,
          duration: 2000,
        });
        void syncPlayerBalances({ force: true });
        setTimeout(() => {
          setGameCreations(prev => {
            const next = { ...prev };
            if (next[creationKey]?.status === 'success') delete next[creationKey];
            return next;
          });
        }, 2500);
      })
      .catch((err) => {
        clearGameCreationTimer(creationKey);
        const MAINTENANCE_MSG = 'Sorry, this game is under maintenance. Please try another game.';
        const status = err?.response?.status;
        const detail = String(err?.response?.data?.detail || err?.response?.data?.message || '').toLowerCase();
        const isDefinitiveFailure = status >= 400 && status < 500 && (
          detail.includes('maintenance') ||
          detail.includes('disabled') ||
          detail.includes('not available')
        );
        const message = isDefinitiveFailure ? MAINTENANCE_MSG : supportMessage;

        removePendingGameAccounts(pendingAccountId);
        setFocusGameAccountId(current => (current === pendingAccountId ? null : current));
        setGameCreations(prev => ({
          ...prev,
          [creationKey]: { status: 'error', game, error: message },
        }));
        toast.error(message, {
          id: `game-create-${creationKey}`,
          duration: 3500,
        });
        setTimeout(() => {
          setGameCreations(prev => {
            if (prev[creationKey]?.error !== message) return prev;
            const next = { ...prev };
            delete next[creationKey];
            return next;
          });
        }, 4000);
        void syncPlayerBalances();
      });
  };

  const balanceUsd = Number(wallet?.cash_balance ?? wallet?.balance ?? 0);
  const myGameAccounts = useMemo(() => {
    const serverAccounts = Array.isArray(data.accounts) ? data.accounts : [];
    const visiblePending = pendingGameAccounts.filter(pending => (
      !serverAccounts.some(serverAcc => accountReplacesPending(pending, serverAcc))
    ));
    return [...visiblePending, ...serverAccounts];
  }, [data.accounts, pendingGameAccounts]);

  const installBrandFallback = activeTenantSlug ? tenantFaviconHref(activeTenantSlug) : '/assets/playemp-icon.svg';
  const loadingBrandLogo = effectiveBranding.intro_logo_url || effectiveBranding.logo_url || installBrandFallback;
  const waitingForPlayerData = !user?.id || loading || playerDataReadyScope !== playerSessionScope;
  if (waitingForPlayerData && recoveryLoading) return <PlayerConnectionSkeleton brandName={effectiveBranding.brand_name || 'your lobby'} />;
  if (waitingForPlayerData) {
    return (
      <div className="player-app min-h-screen flex items-center justify-center px-6">
        <div className="flex flex-col items-center text-center" data-testid="player-lobby-loading">
          <div className="relative mb-5 grid h-28 w-28 place-items-center">
            <span className="absolute inset-0 rounded-full player-lobby-logo-ring" aria-hidden />
            <span className="absolute inset-3 rounded-full bg-black/55 blur-md" aria-hidden />
            <div
              className="relative grid h-24 w-24 place-items-center overflow-hidden rounded-full border"
              style={{
                borderColor: 'rgba(245,215,110,0.88)',
                boxShadow: '0 0 28px rgba(245,215,110,0.46), inset 0 0 18px rgba(245,215,110,0.18)',
                background: 'rgba(0,0,0,0.52)',
              }}
            >
              <SafeImage
                src={loadingBrandLogo}
                alt="Brand logo"
                className="h-full w-full object-cover"
                loading="eager"
                fallback={<Crown className="h-12 w-12 text-[#F5D76E]" />}
              />
            </div>
          </div>
          <p className="display text-2xl font-extrabold text-white">Loading your lobby</p>
          <p className="mt-2 max-w-xs text-sm text-white/60">Syncing your latest games and wallet.</p>
        </div>
        <style>{`
          @keyframes player-lobby-logo-pulse {
            0%, 100% { transform: scale(0.92); opacity: 0.52; box-shadow: 0 0 22px rgba(245,215,110,0.42); }
            50% { transform: scale(1.08); opacity: 1; box-shadow: 0 0 44px rgba(245,215,110,0.72); }
          }
          .player-lobby-logo-ring {
            border: 2px solid rgba(245,215,110,0.72);
            background: radial-gradient(circle, rgba(245,215,110,0.18) 0%, rgba(217,144,18,0.10) 46%, transparent 72%);
            animation: player-lobby-logo-pulse 1.55s ease-in-out infinite;
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="player-app player-home-with-dock min-h-[100dvh] flex flex-col" data-testid="player-app">
      {/* Header — fixed so it stays visible when scrolling.
          Background pulls from the active tenant's `--vl-bg` so the
          header always blends with the page (preventing a black band on
          top of a coloured tenant background). */}
      <div className="player-home-topbar fixed top-0 left-0 right-0 z-50">
        <header className="player-home-header px-3 sm:px-4 py-3" style={{
          background: 'var(--vl-bg, #000000)',
        }}>
        <div className="max-w-6xl mx-auto flex items-center justify-between gap-2 sm:gap-3">
          <button
            type="button"
            onClick={() => navigate(tenantSlug ? `/${tenantSlug}/app/brand` : '/app/brand')}
            className="tap-pop-logo flex items-center gap-2 sm:gap-3 transition active:scale-[0.98] hover:brightness-110 rounded-xl px-0.5 sm:px-1 min-w-0"
            data-testid="header-brand-button"
          >
            <div
              className="player-header-brand-logo flex h-14 w-14 sm:h-16 sm:w-16 shrink-0 items-center justify-center overflow-hidden rounded-full border"
              style={{
                background: 'rgba(255,255,255,0.03)',
                borderColor: 'rgba(255,255,255,0.14)',
                boxShadow: '0 10px 26px rgba(0,0,0,0.36)',
              }}
            >
              {effectiveBranding.logo_url ? (
                <SafeImage
                  src={effectiveBranding.logo_url}
                  alt={effectiveBranding.brand_name}
                  className="w-full h-full object-cover"
                  data-testid="header-brand-logo"
                  loading="eager"
                  fetchPriority="high"
                  fallback={<Crown className="w-8 h-8" style={{ color: 'rgba(255,255,255,0.78)' }} />}
                />
              ) : (
                <Crown className="w-8 h-8" style={{ color: 'rgba(255,255,255,0.78)' }} />
              )}
            </div>
            <div className="hidden sm:block text-left">
              <span
                className="display text-base font-extrabold tracking-wide uppercase"
                style={{ color: '#fff' }}
                data-testid="header-brand-name"
              >
                {effectiveBranding.brand_name || 'Our Lounge'}
              </span>
              <p className="text-[10px] uppercase tracking-[0.2em]" style={{ color: '#fff' }}>
                {effectiveBranding.brand_tagline || 'Player Lounge'}
              </p>
            </div>
          </button>

          <div className="player-header-actions flex items-center gap-1.5 sm:gap-3 min-w-0">
            <div
              className="tap-pop-balance player-wallet-pill rounded-2xl pl-1.5 sm:pl-2 pr-2.5 sm:pr-4 py-1.5 flex items-center gap-1.5 sm:gap-2.5 min-w-0"
              style={{
                background: 'linear-gradient(135deg, rgba(168,85,247,0.18), rgba(109,40,217,0.10))',
                border: '1px solid rgba(168,85,247,0.5)',
              }}
              data-testid="wallet-pill"
            >
              <button
                type="button"
                onClick={() => { tap(); openBalanceDetail(); }}
                className="flex items-center gap-1.5 sm:gap-2.5 transition active:scale-[0.97] min-w-0"
                title="View balance details"
                aria-label="View balance details"
              >
                <div
                  className="player-wallet-icon w-8 h-8 sm:w-9 sm:h-9 rounded-xl flex items-center justify-center shrink-0"
                  style={{
                    background: 'radial-gradient(circle at 30% 30%, #F0ABFC 0%, #A855F7 45%, #6D28D9 95%)',
                    boxShadow: 'inset 0 0 10px rgba(255,255,255,0.4), 0 0 14px rgba(168,85,247,0.6)'
                  }}
                >
                  <Wallet className="w-5 h-5" style={{ color: '#fff' }} />
                </div>
                <div className="text-left leading-tight min-w-0">
                  <p className="player-wallet-label text-[9px] sm:text-[10px] uppercase tracking-wider" style={{ color: '#fff' }}>Balance</p>
                  <p
                    className="player-wallet-amount display truncate text-base sm:text-lg font-extrabold"
                    style={{
                      color: 'var(--vl-neon, #22FF88)',
                      textShadow: '0 0 12px color-mix(in oklab, var(--vl-neon, #22FF88) 55%, transparent)'
                    }}
                    data-testid="header-balance-usd"
                  >
                    ${balanceUsd.toFixed(2)}
                  </p>
                </div>
              </button>
              <span
                className="player-wallet-fp display text-[11px] sm:text-xs font-extrabold whitespace-nowrap ml-0.5 sm:ml-1 select-none"
                style={{ color: '#FACC15', textShadow: '0 0 10px rgba(250,204,21,0.45)' }}
                data-testid="header-balance-fp"
                title="Freeplay credits"
              >
                {formatFP(wallet?.play_credits ?? wallet?.fp_credits ?? 0)} FP
              </span>
              {balanceCoachVisible && (
                <button
                  type="button"
                  onClick={() => { tap(); openBalanceDetail(); }}
                  className="balance-coach-pop"
                  data-testid="balance-coach-pop"
                  aria-label="Open add cash and cashout options"
                >
                  <span className="balance-coach-kicker">Tap here</span>
                  <span className="balance-coach-title">Add &amp; cashout credits</span>
                  <span className="balance-coach-copy">Tap on here to add and cashout your credits.</span>
                </button>
              )}
            </div>

            <HeaderAlertsButton
              unread={notifUnread}
              open={homeAlertsOpen}
              onOpenChange={handleHomeAlertsOpenChange}
              loading={homeAlertsLoading}
              items={homeAlertItems}
              onItemClick={markHomeAlertRead}
              onMarkAll={markHomeAlertsReadAll}
              onViewAll={openAllAlertsFromHome}
            />

            <ProfileTrigger
              user={user}
              badge={badge}
              onOpen={() => { tap(); openProfile(); }}
            />
          </div>
        </div>
      </header>
      </div>
      <div className="player-home-header-spacer" aria-hidden="true" />

      {/* App and notification setup; completed/ineligible cards stay hidden. */}
      {HOME_PROMOTION_NUDGES_ENABLED && <TimedHomeNudgeStack>
        <AllowNotificationsNudge tenantSlug={tenantSlug} userId={user?.id} />
        <InstallAppNudge
          brandLogo={effectiveBranding.logo_url || effectiveBranding.intro_logo_url || installBrandFallback}
          brandName={effectiveBranding.brand_name}
          tenantSlug={activeTenantSlug}
          onOpenAndroidApp={openAndroidAppDownloadPage}
          onOpenShortcutInstall={openShortcutInstallHelper}
        />
      </TimedHomeNudgeStack>}
      <div className="max-w-6xl mx-auto w-full px-3 sm:px-4 pt-2">
        <PromoBanners hidePhoneVerify={isPhoneVerified(user) || badge?.phone_verified === true} />
      </div>

      {/* Explore & Earn — quick action below banners */}
      <div className="max-w-6xl mx-auto w-full px-3 sm:px-4 pt-3 space-y-3">
        <button
          onClick={() => navigate(tenantSlug ? `/${tenantSlug}/app/explore` : '/app/explore')}
          className="explore-earn-card w-full flex items-center gap-3 sm:gap-4 rounded-[24px] px-3 sm:px-4 py-3 transition active:scale-[0.99] hover:brightness-110 overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, rgba(255,217,61,0.14) 0%, rgba(34,255,136,0.12) 100%)',
            border: '1px solid rgba(255,217,61,0.45)',
            boxShadow: '0 12px 28px rgba(244,180,0,0.16), inset 0 1px 0 rgba(255,255,255,0.08)'
          }}
          data-testid="explore-earn-cta"
        >
          <span
            className="explore-gift-wrap shrink-0 -my-3 sm:-my-4 -ml-1 flex items-center justify-center gift-shake"
            aria-hidden
          >
            <ExploreGiftArt />
          </span>

          <div className="flex-1 text-left min-w-0">
            <p
              className="display font-extrabold text-base sm:text-lg leading-tight"
              style={{ color: '#FFFFFF' }}
            >
              Explore &amp; Earn
            </p>
            <p
              className="text-xs sm:text-sm leading-tight mt-0.5 truncate"
              style={{ color: 'rgba(255,255,255,0.72)' }}
            >
              Freeplay bonuses and rewards
            </p>
          </div>

          <ChevronDown
            className="w-5 h-5 -rotate-90 shrink-0"
            style={{ color: '#FFD93D' }}
          />
        </button>

      </div>

      {/* Main */}
      <main id="my-games-section" className="flex-1 max-w-6xl mx-auto w-full px-3 sm:px-4 pt-3 pb-32">
        <MyGamesSection
          accounts={myGameAccounts}
          games={data.games}
          wallet={wallet}
          favorites={favorites}
          onToggleFav={toggleFavorite}
          onAdd={() => openAddGame()}
          refresh={fetchData}
          onBackgroundSync={syncPlayerBalances}
          onAccountPatch={patchPlayerAccount}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          focusAccountId={focusGameAccountId}
        />
      </main>

      <PlayerLiquidNavigation active="home" hidden={walletOpen || supportMenuOpen || chatOpen || addGameOpen || resetPwOpen || editProfileOpen || profileOpen || balanceDetailOpen || announcementsOpen || notificationsOpen || notificationDetailOpen || homeAlertsOpen || achievementsOpen} />

      {/* Floating Support FAB — Headset/message style with animations */}
      <button
        onClick={() => { tap(); openSupportMenu(); }}
        className="fixed z-40 rounded-full flex items-center justify-center transition active:scale-95 group support-fab-anim support-fab-clean"
        style={{
          right: 'max(14px, env(safe-area-inset-right))',
          bottom: 'calc(var(--player-support-bottom, 22px) + env(safe-area-inset-bottom))',
        }}
        data-testid="support-fab"
        title="Support — chat with our team"
        aria-label="Open support — chat with our team"
      >
        {/* Animated ripple rings */}
        <span aria-hidden className="support-fab-ripple" />
        <span aria-hidden className="support-fab-ripple support-fab-ripple-delay" />

        {/* Messenger-style chat-bubble glyph — communicates "send us a
            message" / live-chat support, swapped from the previous
            headphones icon per branding. */}
        <svg
          viewBox="0 0 24 24"
          className="w-7 h-7 support-fab-icon"
          style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.45))' }}
          aria-hidden
          focusable="false"
        >
          <path
            fill="#FFFFFF"
            d="M12 2C6.48 2 2 6.14 2 11.25c0 2.86 1.4 5.42 3.6 7.13V22l3.3-1.81c.99.27 2.04.42 3.1.42 5.52 0 10-4.14 10-9.36S17.52 2 12 2zm1.05 12.55l-2.55-2.72-4.97 2.72 5.46-5.79 2.62 2.72 4.9-2.72-5.46 5.79z"
          />
        </svg>

        {chatUnread > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[24px] h-6 px-1.5 text-white text-[11px] font-bold rounded-full flex items-center justify-center support-fab-unread"
            style={{ background: '#FF3D71', boxShadow: '0 0 12px rgba(255,61,113,0.7)', border: '2px solid #fff' }}
          >
            {chatUnread > 9 ? '9+' : chatUnread}
          </span>
        )}
      </button>

      {requiresNameOnboarding && (
        <RequiredNameOnboarding
          key={user.id}
          user={user}
          branding={effectiveBranding}
        />
      )}

      {/* Support menu sheet */}
      <Sheet open={supportMenuOpen} onOpenChange={(o) => { if (o) openSupportMenu(); else closeSupportMenu(); }}>
        <SheetContent
          showClose={false}
          side="right"
          className="player-app p-0 border-l w-full sm:max-w-sm"
          style={{ background: 'var(--vl-bg, #000000)', borderColor: 'rgba(34,255,136,0.3)' }}
        >
          <SupportMenu
            branding={effectiveBranding}
            chatUnread={chatUnread}
            agentUnread={chatAgentUnread}
            onPickAgent={(agent) => {
              moveSupportToChat(agent || null);
            }}
            onLiveChat={() => {
              moveSupportToChat(null);
            }}
            onCommunity={moveSupportToCommunity}
            onClose={() => closeSupportMenu()}
          />
        </SheetContent>
      </Sheet>

      {/* Login announcement popup */}
      <LoginAnnouncementPopup userId={user?.id} tenantSlug={tenantSlug} enabled={announcementCycle} />
      <PlayerApkUpdatePrompt
        tenantSlug={activeTenantSlug}
        brandLogo={effectiveBranding.logo_url || effectiveBranding.intro_logo_url}
        brandName={effectiveBranding.brand_name}
      />

      {/* Chat sheet */}
      <Sheet open={chatOpen} onOpenChange={(o) => { if (o) openChat(); else closeChat(); }}>
        <SheetContent
          side="right"
          showClose={false}
          className="player-app p-0 border-l w-full sm:max-w-md overflow-hidden overscroll-none"
          style={{
            background: 'var(--vl-bg, #000000)',
            borderColor: 'rgba(34,255,136,0.3)',
            top: 'var(--player-chat-viewport-top, 0px)',
            bottom: 'auto',
            height: 'var(--player-chat-viewport-height, 100dvh)',
            maxHeight: 'var(--player-chat-viewport-height, 100dvh)',
            minHeight: 0,
            overscrollBehavior: 'none',
          }}
        >
          <ChatPanel
            user={user}
            selectedAgent={selectedChatAgent}
            openCommunity={openCommunityFromSupport}
            onRead={() => fetchUnread()}
            onBackToAgents={moveChatToSupport}
            onCommunityBackToAgents={moveChatToSupport}
          />
        </SheetContent>
      </Sheet>

      {/* Announcements panel */}
      <Sheet open={announcementsOpen} onOpenChange={(o) => { if (o) openAnnouncements(); else closeAnnouncements(); }}>
        <SheetContent
          side="right"
          className="player-app p-0 border-l w-full sm:max-w-md overflow-hidden"
          style={{ background: 'var(--vl-bg, #000000)', borderColor: 'rgba(255,217,61,0.35)' }}
        >
          <SheetHeader className="sr-only"><SheetTitle>Announcements</SheetTitle></SheetHeader>
          <AnnouncementsPanel close={() => closeAnnouncements()} />
        </SheetContent>
      </Sheet>

      {/* Notifications panel */}
      <Sheet open={notificationsOpen} onOpenChange={(o) => { if (o) openAlertsPanel(); else closeAlertsPanel(); }}>
        <SheetContent
          side="right"
          className="player-app p-0 border-l w-full sm:max-w-md overflow-hidden"
          style={{ background: 'var(--vl-bg, #000000)', borderColor: 'rgba(34,255,136,0.35)' }}
        >
          <SheetHeader className="sr-only"><SheetTitle>Notifications</SheetTitle></SheetHeader>
          <NotificationsPanel
            close={() => closeAlertsPanel()}
            detailOpen={notificationDetailOpen}
            detailItem={notificationDetailItem}
            onOpenDetail={openNotificationDetail}
            onCloseDetail={closeNotificationDetail}
            onRead={() => {
              setNotifUnread(0);
              toast.dismiss();
            }}
          />
        </SheetContent>
      </Sheet>

      {/* Achievements panel */}
      <Sheet open={achievementsOpen} onOpenChange={(o) => { if (o) openAchievements(); else closeAchievements(); }}>
        <SheetContent
          side="right"
          className="player-app p-0 border-l w-full sm:max-w-md overflow-hidden"
          style={{ background: 'var(--vl-bg, #000000)', borderColor: 'rgba(249,115,22,0.35)' }}
        >
          <SheetHeader className="sr-only"><SheetTitle>Achievements</SheetTitle></SheetHeader>
          <AchievementsPanel close={() => closeAchievements()} />
        </SheetContent>
      </Sheet>

      {/* Wallet — FULL-SCREEN white panel (step 1: method, step 2: amount) */}
      {walletOpen && (
        <FullScreenWhitePanel onClose={() => closeOverlayWithBack('wallet', () => { setWalletOpen(false); setCashoutStage('choice'); })} testid="wallet-fs">
          {walletMode === 'withdrawal' && cashoutStage === 'choice' ? (
            <CashoutSpeedPicker
              onSelect={startCashout}
              expressFeePercent={walletRules?.withdrawal_rules?.express_fee_percent ?? 8}
              onClose={() => closeOverlayWithBack('wallet', () => { setWalletOpen(false); setCashoutStage('choice'); })}
            />
          ) : walletMode === 'withdrawal' && cashoutSpeed === 'priority' ? (
            <PriorityPayoutFlow wallet={wallet} walletRules={walletRules}
              enabled={walletRules?.withdrawal_rules?.priority_enabled === true}
              onBack={() => setCashoutStage('choice')}
              close={() => closeOverlayWithBack('wallet', () => { setWalletOpen(false); setCashoutStage('choice'); })}
              refresh={fetchData}/>
          ) : (
            <WalletFlow
              mode={walletMode}
              cashoutSpeed={cashoutSpeed}
              setMode={(m) => { setWalletMode(m); setWalletStep(1); setWalletMethod(null); }}
              step={walletStep}
              setStep={setWalletStep}
              method={walletMethod}
              setMethod={setWalletMethod}
              wallet={wallet}
              walletRules={walletRules}
              methods={data.methods}
              refresh={fetchData}
              checkAuth={checkAuth}
              close={() => closeOverlayWithBack('wallet', () => { setWalletOpen(false); setCashoutStage('choice'); })}
              onBackToCashoutChoice={() => {
                setWalletStep(1);
                setWalletMethod(null);
                setCashoutStage('choice');
              }}
              branding={effectiveBranding}
              onOpenSupport={() => {
                closeOverlayWithBack('wallet', () => setWalletOpen(false));
                window.setTimeout(() => openSupportMenu(), 120);
              }}
            />
          )}
        </FullScreenWhitePanel>
      )}

      {/* Full-screen Profile */}
      {profileOpen && (
        <ProfileFullScreen
          user={user}
          badge={badge}
          tenantSlug={activeTenantSlug}
          nativeRuntime={isNativePlayerRuntime()}
          identityFocusKey={profileIdentityFocusKey}
          onClose={() => closeProfile()}
          onEditProfile={() => openEditProfile({ restoreTo: 'profile' })}
          onEditPhone={() => openEditProfile({ restoreTo: 'profile', mode: 'phone-verify' })}
          onResetPassword={() => {
            withOverlayHistorySuppressed(() => {
              setProfileOpen(false);
              setTimeout(() => openResetPassword({ restoreTo: 'profile' }), 200);
            });
          }}
          onContactSupport={() => {
            closeOverlayWithBack('profile', () => setProfileOpen(false));
            window.setTimeout(() => openSupportMenu(), 120);
          }}
          onOpenAndroidApp={openAndroidAppDownloadPage}
          showAndroidAppInstall={canDownloadTenantApk}
          showShortcutInstall
          onLogout={handleLogout}
        />
      )}

      {/* Balance details — modal showing wallet + total in games */}
      <Dialog modal={false} open={balanceDetailOpen} onOpenChange={(o) => { if (o) openBalanceDetail(); else closeBalanceDetail(); }}>
        <DialogContent
          overlayClassName="!z-[79] !bg-black/60 backdrop-blur-md"
          className="!fixed !inset-0 !left-0 !top-0 z-[80] !h-[100dvh] !max-h-[100dvh] !w-screen !max-w-none !translate-x-0 !translate-y-0 gap-0 rounded-none border-0 bg-white p-0 shadow-none sm:rounded-none [&>button]:hidden"
          style={{ background: '#fff' }}
          aria-describedby="balance-detail-desc"
          onPointerDownOutside={keepWalletTransferLayerOpen}
          onInteractOutside={keepWalletTransferLayerOpen}
        >
          <DialogTitle className="sr-only">Balance Details</DialogTitle>
          <span id="balance-detail-desc" className="sr-only">View your wallet balance, freeplay credits, total game balance, and grand total.</span>
          <BalanceDetailPanel
            wallet={wallet}
            accounts={data.accounts}
            games={data.games}
            onPurchase={() => { closeBalanceDetail(); setTimeout(openPurchase, 200); }}
            onCashout={() => { closeBalanceDetail(); setTimeout(openCashout, 200); }}
            onWalletChanged={() => syncPlayerBalances({ force: true })}
            onVerificationRequired={openIdentityVerificationFromWallet}
            close={() => closeBalanceDetail()}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Profile dialog */}
      <Dialog open={editProfileOpen} onOpenChange={(o) => { if (o) openEditProfile(); else closeEditProfile(); }}>
        <DialogContent
          overlayClassName="!z-[80] !bg-black/70 backdrop-blur-lg"
          className="player-app max-w-md border z-[81] p-5 sm:p-6"
          onOpenAutoFocus={(event) => {
            if (editProfileMode === 'phone-verify') event.preventDefault();
          }}
          style={HALF_SHEET_DIALOG_STYLE}
        >
          <DialogHeader>
            <DialogTitle className="display text-xl font-extrabold" style={{ color: '#fff' }}>
              {editProfileMode === 'phone-verify' ? 'Verify Phone Number' : 'Edit Profile'}
            </DialogTitle>
          </DialogHeader>
          <EditProfilePanel
            user={user}
            checkAuth={checkAuth}
            close={() => closeEditProfile()}
            initialVerifyOpen={editProfileMode === 'phone-verify'}
            verificationBackSignal={phoneVerificationBackSignal}
            onVerificationScreenOpen={() => pushOverlayHistory('phone-verification')}
            onVerificationScreenClose={(closeScreen) => {
              closeOverlayWithBack('phone-verification', closeScreen);
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Add Game dialog — full-screen */}
      <Dialog open={addGameOpen} onOpenChange={(o) => { if (o) openAddGame(); else closeAddGame(); }}>
        <DialogContent
          overlayClassName="!bg-black/60 backdrop-blur-md"
          showClose={false}
          className="player-app add-game-dialog border-0 p-6 sm:p-8 relative flex flex-col overflow-hidden"
          style={{
            background: 'var(--vl-bg, #000000)',
            color: 'var(--vl-text-primary, #FFFFFF)',
            boxShadow: '0 25px 60px rgba(0,0,0,0.8), 0 0 0 1px rgba(34,211,238,0.18)',
            position: 'fixed',
            top: 0,
            left: 0,
            transform: 'none',
            width: '100vw',
            height: '100dvh',
            maxWidth: '100vw',
            maxHeight: '100dvh',
            borderRadius: 0,
            margin: 0,
          }}
        >
          {/* Custom round cyan close button (slightly smaller) */}
          <button
            onClick={() => closeAddGame()}
            className="absolute right-3 top-3 rounded-full flex items-center justify-center transition active:scale-95 z-20 hover:scale-105"
            style={{
              width: '42px',
              height: '42px',
              background: 'rgba(34,211,238,0.12)',
              border: '2.5px solid #22D3EE',
              boxShadow: '0 0 0 3px rgba(34,211,238,0.18), 0 0 18px rgba(34,211,238,0.55), inset 0 0 12px rgba(34,211,238,0.18)'
            }}
            data-testid="add-game-close"
            aria-label="Close"
          >
            <X className="w-5 h-5" style={{ color: '#22D3EE' }} strokeWidth={3} />
          </button>

          <DialogHeader className="items-center shrink-0">
            <DialogTitle className="display text-2xl sm:text-3xl font-extrabold text-center" style={{ color: '#FFFFFF' }}>Add a game</DialogTitle>
          </DialogHeader>
          <div className="flex-1 min-h-0 flex flex-col">
            <AddGamePanel
              games={data.games}
              accounts={myGameAccounts}
              refresh={fetchData}
              close={() => closeAddGame()}
              gameCreations={gameCreations}
              onStartCreate={startGameCreation}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset Account Password dialog */}
      <Dialog open={resetPwOpen} onOpenChange={(o) => { if (o) openResetPassword(); else closeResetPassword(); }}>
        <DialogContent
          overlayClassName="!z-[80] !bg-black/55 backdrop-blur-md"
          className="player-app max-w-md border z-[81] p-5 sm:p-6"
          style={HALF_SHEET_DIALOG_STYLE}
        >
          <DialogHeader>
            <DialogTitle className="display text-xl font-extrabold" style={{ color: '#fff' }}>Reset Account Password</DialogTitle>
          </DialogHeader>
          <ResetPasswordPanel close={() => closeResetPassword()} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ExploreGiftArt() {
  return (
    <div
      className="explore-gift-art"
      role="img"
      aria-label="Gift rewards"
      data-testid="explore-earn-logo"
    >
      <img
        src={referAndEarnGiftCutout}
        alt=""
        aria-hidden="true"
        className="explore-gift-gif"
        loading="eager"
      />
    </div>
  );
}

function ProfileTrigger({ user, badge, onOpen }) {
  const avatarUrl = user?.avatar_url;
  const showBadge = !!badge?.show_badge;
  const tier = badge?.tier || {};
  return (
    <button
      onClick={onOpen}
      className="player-header-profile tap-pop-profile flex h-13 w-13 items-center justify-center rounded-full p-1 transition active:scale-[0.97]"
      style={{
        width: 52,
        height: 52,
        background: 'radial-gradient(circle at 32% 22%, rgba(255,255,255,0.28), rgba(56,189,248,0.18) 42%, rgba(10,18,26,0.88) 100%)',
        border: '1px solid rgba(56,189,248,0.45)',
        boxShadow: '0 10px 24px rgba(0,0,0,0.30), 0 0 18px rgba(56,189,248,0.24), inset 0 0 10px rgba(255,255,255,0.12)',
      }}
      data-testid="profile-menu-trigger"
      title={showBadge ? `${tier.label || 'Verified'} — phone verified` : 'Open profile'}
    >
      <span className="relative">
        <span
          className="player-header-avatar w-11 h-11 rounded-full flex items-center justify-center overflow-hidden"
          style={{
            background: avatarUrl ? '#0B0F0C' : 'radial-gradient(circle at 30% 25%, #FFFFFF 0%, #38BDF8 35%, #0EA5E9 75%, #075985 100%)',
            boxShadow: '0 0 12px rgba(56,189,248,0.65), inset 0 0 8px rgba(255,255,255,0.45)',
            border: '2px solid #FFFFFF'
          }}
        >
          {avatarUrl
            ? <SafeImage src={avatarUrl} alt="avatar" className="w-full h-full object-cover" data-testid="profile-avatar-img" fallback={<CircleUser className="w-6 h-6" style={{ color: '#fff' }} />} />
            : <CircleUser className="w-6 h-6" style={{ color: '#fff' }} />}
        </span>
        {showBadge && (
          <span
            className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full flex items-center justify-center"
            style={{
              background: 'radial-gradient(circle at 30% 25%, #FFF4B8 0%, var(--vl-brand-primary, #D4AF37) 48%, #8B5E00 100%)',
              border: '2px solid #0B0F0C',
              boxShadow: '0 0 8px color-mix(in oklab, var(--vl-brand-primary, #D4AF37) 72%, transparent), inset 0 1px 1px rgba(255,255,255,0.55)'
            }}
            data-testid="player-verified-badge"
            title={`${tier.label || 'Verified'} — phone verified`}
          >
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M20 6L9 17l-5-5" stroke="#FFFFFF" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </span>
        )}
      </span>
    </button>
  );
}


/* -------------------- BALANCE DETAIL PANEL -------------------- */
// Extracted to ./components/ProfilePanels.jsx (along with ResetPasswordPanel).



/* -------------------- CHAT PANEL -------------------- */
/* -------------------- HEADER ICON BUTTON (Announcements / Notifications / Achievements) -------------------- */
function HeaderIconBtn({ icon: Icon, color, title, onClick, badge, testid }) {
  return (
    <button
      onClick={onClick}
      title={title}
      aria-label={title}
      data-testid={testid}
      className="relative w-10 h-10 rounded-xl flex items-center justify-center transition active:scale-95 hover:brightness-110"
      style={{
        background: `linear-gradient(135deg, ${color}22, ${color}0A)`,
        border: `1px solid ${color}66`,
        boxShadow: `0 0 12px ${color}33, inset 0 1px 0 rgba(255,255,255,0.06)`,
      }}
    >
      <Icon className="w-5 h-5" style={{ color }} strokeWidth={2.3} />
      {badge > 0 && (
        <span
          className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full flex items-center justify-center display text-[10px] font-extrabold"
          style={{ background: '#EF4444', color: '#FFFFFF', border: '1.5px solid #0B130E' }}
          data-testid={`${testid}-badge`}
        >
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </button>
  );
}

/* -------------------- ANNOUNCEMENTS / NOTIFICATIONS / ACHIEVEMENTS -------------------- */
// Extracted to ./components/SidePanels.jsx — see imports at top of file.


/* -------------------- SOCIAL SUPPORT HALF-SCREEN OVERLAY -------------------- */
function SocialSupportOverlay({ branding, close }) {
  const socials = branding?.socials || {};
  const items = [
    { key: 'instagram', label: 'Instagram', handleDomain: 'instagram.com', gradient: 'linear-gradient(135deg, #833AB4, #E1306C 50%, #FCB045 100%)', Icon: InstagramGlyph, cta: 'Follow' },
    { key: 'youtube',   label: 'YouTube',   handleDomain: 'youtube.com',   gradient: '#FF0000',                                                          Icon: YoutubeGlyph,   cta: 'Subscribe' },
    { key: 'tiktok',    label: 'TikTok',    handleDomain: 'tiktok.com',    gradient: '#000000',                                                          Icon: TikTokGlyph,    cta: 'Follow' },
    { key: 'facebook',  label: 'Facebook',  handleDomain: 'facebook.com',  gradient: '#1877F2',                                                          Icon: FacebookGlyph,  cta: 'Message' },
    { key: 'discord',   label: 'Discord',   handleDomain: 'discord.gg',    gradient: '#5865F2',                                                          Icon: DiscordGlyph,   cta: 'Join' },
    { key: 'telegram',  label: 'Telegram',  handleDomain: 't.me',          gradient: '#26A5E4',                                                          Icon: TelegramGlyph,  cta: 'Open' },
  ].filter(i => socials[i.key]);

  const emailRow = socials.support_email ? { href: `mailto:${socials.support_email}`, email: socials.support_email } : null;

  const open = (url) => window.open(url, '_blank', 'noopener,noreferrer');

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center px-4"
      style={{ background: 'rgba(3,5,12,0.55)', backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)' }}
      onClick={close}
      data-testid="social-support-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-md max-h-[62vh] overflow-hidden rounded-t-3xl sm:rounded-3xl flex flex-col animate-fade-in-up"
        style={{
          background: 'var(--vl-bg, #000000)',
          border: '1px solid rgba(34,255,136,0.32)',
          boxShadow: '0 -14px 40px rgba(0,0,0,0.55), 0 0 40px rgba(34,255,136,0.16)'
        }}
      >
        <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b" style={{ borderColor: 'rgba(168,85,247,0.3)' }}>
          <div>
            <p className="display text-lg font-extrabold" style={{ color: '#fff' }}>Social Media Support</p>
            <p className="text-xs" style={{ color: '#C4B5FD' }}>Reach us on any of these channels</p>
          </div>
          <button onClick={close} className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.06)', color: '#fff' }} data-testid="social-support-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="overflow-y-auto p-4 space-y-2" data-testid="social-support-list">
          {items.length === 0 && !emailRow && (
            <div className="py-10 text-center">
              <Megaphone className="w-10 h-10 mx-auto mb-3 text-purple-300 opacity-60" />
              <p className="display font-bold text-white/80">No social channels configured</p>
              <p className="text-sm text-white/50 mt-1">An admin can add them in Branding.</p>
            </div>
          )}
          {items.map(item => {
            const url = socials[item.key];
            return (
              <button
                key={item.key}
                onClick={() => open(url)}
                className="w-full flex items-center gap-3 rounded-2xl p-3 transition active:scale-[0.99] hover:brightness-110"
                style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}
                data-testid={`social-support-${item.key}`}
              >
                <span className="shrink-0 w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: item.gradient, boxShadow: `0 4px 14px rgba(0,0,0,0.45)` }}>
                  <item.Icon />
                </span>
                <div className="flex-1 min-w-0 text-left">
                  <p className="font-bold text-white">{item.label}</p>
                  <p className="text-xs text-white/60 truncate">{url.replace(/^https?:\/\//, '')}</p>
                </div>
                <span className="shrink-0 display text-xs font-bold px-3 py-1.5 rounded-full" style={{ background: 'rgba(167,139,250,0.18)', color: '#DDD6FE', border: '1px solid rgba(167,139,250,0.5)' }}>{item.cta}</span>
              </button>
            );
          })}
          {emailRow && (
            <a href={emailRow.href} className="flex items-center gap-3 rounded-2xl p-3 transition active:scale-[0.99] hover:brightness-110"
              style={{ background: 'rgba(168,85,247,0.12)', border: '1px solid rgba(168,85,247,0.45)' }}
              data-testid="social-support-email">
              <span className="shrink-0 w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #A855F7, #6B21A8)' }}>
                <Mail className="w-5 h-5 text-white" />
              </span>
              <div className="flex-1 min-w-0 text-left">
                <p className="font-bold text-white">Support Email</p>
                <p className="text-xs text-white/70 truncate">{emailRow.email}</p>
              </div>
              <span className="shrink-0 display text-xs font-bold px-3 py-1.5 rounded-full" style={{ background: 'rgba(167,139,250,0.25)', color: '#DDD6FE', border: '1px solid rgba(167,139,250,0.6)' }}>Email</span>
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
